import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { formatMoney, currencySymbol, formatWeight, canonicalWeight } from "@/lib/utils";
import { runSharePdfJob, shareErrorMessage } from "@/lib/sharePdfJob";
import {
  Lock, KeyRound, Search, Settings2, ShoppingBag, ChevronRight, ChevronDown, ChevronUp,
  Coins, Sparkles, X, Tag, Check, Loader2, Share2, Image as ImageIcon, Download,
  FileText, Pencil, Clock, Camera, Trash2, AlertTriangle, Link2, RefreshCw, Copy, Plus,
  ArrowUpDown, SlidersHorizontal,
} from "lucide-react";

// ─── HTTP — public lane uses a fresh axios with no JWT header ───────
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api`, timeout: 20000 });

const fmtMoney = formatMoney; // CS 21.4 — shared currency helper

// ─── Client-side selling engine (mirrors backend compute_selling) ───
const DEFAULT_PURITY = { 9: 0.375, 14: 0.583, 18: 0.75, 950: 0.95 };

// CS 21.9 / Turn D-1 -- Client-side shape abbreviation resolver. Prefers the
// server-seeded `standard.shape_abbreviations` dict (keyed by raw shape name,
// case-insensitive). Fallback: first 2 letters uppercase; if two shapes on the
// same piece would collapse to the same abbreviation (e.g. Trapeze/Triangle
// both -> TR), fall back to the full raw name for the colliding shapes.
function buildAbbrResolver(ratecard, pieceLines) {
  const seeded = (ratecard?.standard?.shape_abbreviations) || {};
  const seededLC = {};
  for (const [k, v] of Object.entries(seeded)) seededLC[String(k).toLowerCase()] = v;
  // Compute default abbreviations for this piece's raw shapes and detect collisions.
  const rawShapes = Array.from(new Set((pieceLines || [])
    .map((l) => String(l.shape || "").trim())
    .filter(Boolean)));
  const draft = {};
  for (const s of rawShapes) {
    const hit = seededLC[s.toLowerCase()];
    draft[s] = hit || (s.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || s);
  }
  // Collision-safe fallback: any abbr that maps back to >1 raw shape uses full name.
  const counts = {};
  for (const abbr of Object.values(draft)) counts[abbr] = (counts[abbr] || 0) + 1;
  const finalMap = {};
  for (const [raw, abbr] of Object.entries(draft)) {
    finalMap[raw] = counts[abbr] > 1 ? raw : abbr;
  }
  return (rawShape) => {
    if (!rawShape) return "—";
    return finalMap[String(rawShape).trim()] || String(rawShape);
  };
}

// CS 21.7 — schema-agnostic diamond lookup (v2 brackets_by_shape first,
// then legacy standard.brackets[].rates[shape], then legacy sub-tables).
// side: "sell" (default) or "cost".
// CS 21.44 -- Between-bracket avg values SNAP to the next-higher bracket
// (or the top bracket when the avg is above every floor). Only shapes
// with truly no brackets defined return null, matching backend
// `pick_bracket_v3` after CS 21.44. This makes the frontend drawer +
// mirror compute agree with the backend pricing engine on gap cases.
function _pickBracket(rows, avgCt) {
  if (!rows || !rows.length) return null;
  const avg = Number(avgCt);
  if (!Number.isFinite(avg)) return null;
  // Pass 1: strict containment (matches backend pick_bracket_v3 first pass).
  const valid = [];
  for (const b of rows) {
    const rawLo = (b?.min !== undefined && b?.min !== null && b?.min !== "") ? b.min : (b?.range || "").split("-")[0];
    const rawHi = (b?.max !== undefined && b?.max !== null && b?.max !== "") ? b.max : (b?.range || "").split("-")[1];
    const lo = parseFloat(rawLo);
    const hi = parseFloat(rawHi);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) continue;
    if (avg >= lo && avg <= hi) return b;
    valid.push({ lo, hi, b });
  }
  if (!valid.length) return null;
  // Pass 2 (CS 21.44): SNAP to next-higher bracket by lo.
  valid.sort((a, b) => a.lo - b.lo);
  for (const v of valid) {
    if (v.lo >= avg) return v.b;
  }
  // Pass 3: above every floor -- return the bracket with the largest max.
  let top = valid[0];
  for (const v of valid) if (v.hi > top.hi) top = v;
  return top.b;
}
function lookupDiamondRate(ratecard, table, shape, avgCt, side = "sell") {
  if (!ratecard || !avgCt) return { rate: null, bracket: null };
  if (table === "standard") {
    const std = ratecard.standard || {};
    if (std.brackets_by_shape) {
      const b = _pickBracket(std.brackets_by_shape[shape], avgCt);
      return b ? { rate: b[side] ?? null, bracket: b } : { rate: null, bracket: null };
    }
    const b = _pickBracket(std.brackets, avgCt);
    if (!b) return { rate: null, bracket: null };
    return { rate: b.rates?.[shape]?.[side] ?? null, bracket: b };
  }
  const b = _pickBracket(ratecard[table], avgCt);
  return b ? { rate: b[side] ?? null, bracket: b } : { rate: null, bracket: null };
}
function lookupDiamondSell(ratecard, table, shape, avgCt, tier = "b2b") {
  // Prefer tier-scoped sell (sell_b2b / sell_customer); backend has legacy
  // fallback to plain `sell` for pre-F-1 ratecards.
  const r = lookupDiamondRate(ratecard, table, shape, avgCt, `sell_${tier}`);
  if (r.rate != null) return r.rate;
  return lookupDiamondRate(ratecard, table, shape, avgCt, "sell").rate;
}
// Best-effort shape resolver on the client (mirrors _resolve_ratecard_shape).
// CS 21.17 -- consults `ratecard.standard.aliases` FIRST (case-insensitive,
// trimmed). Previous impl skipped aliases entirely and fell through to a
// hardcoded "Fancy fallback" that had a typo ("Asth/Heart/Marquise" instead
// of "Ashth/Heart/Marquise"), silently misrouting any raw shape that only
// matched via alias. Reproducer: raw "Ashth Cut" -> should route to
// "Ashth/Heart/Marquise" via alias "ashth cut", never to Oval/Emerald.
function resolveShape(ratecard, raw) {
  if (!raw) return null;
  const norm = String(raw).trim().toLowerCase();
  if (!norm) return null;
  const std = ratecard?.standard || {};
  const shapes = std.shapes || [];
  // 1. Exact shape-name match (case-insensitive).
  for (const cand of shapes) {
    if (String(cand).trim().toLowerCase() === norm) return cand;
  }
  // 2. Alias match -- HO-owned routing dictionary. Case-insensitive + trim.
  const aliases = std.aliases || {};
  for (const [shape, list] of Object.entries(aliases)) {
    if (!Array.isArray(list)) continue;
    for (const a of list) {
      if (String(a || "").trim().toLowerCase() === norm) return shape;
    }
  }
  // 3. CS 21.53 -- Abbreviation fallback. `shape_abbreviations` maps raw
  // shape names ("Pear", "Ashth Cut") to their display code ("PS", "AC").
  // If the raw upload string matches a code, resolve the corresponding
  // raw name through aliases. Same rule as backend `resolve_shape_by_alias`.
  // Fixes the drawer showing "PS" but returning rate=0 because "PS" wasn't
  // in the alias list.
  const abbrs = std.shape_abbreviations || {};
  if (abbrs && typeof abbrs === "object") {
    for (const [rawName, code] of Object.entries(abbrs)) {
      if (String(code || "").trim().toLowerCase() !== norm) continue;
      if (!rawName) continue;
      const inner = String(rawName).trim().toLowerCase();
      // Recurse ONCE through the raw name; guard against a cycle where
      // an abbr equals itself.
      if (inner === norm) continue;
      const hit = resolveShape(ratecard, rawName);
      if (hit) return hit;
    }
    // CS 21.76 -- FORWARD abbrev path. If raw is a full shape name whose
    // canonical CODE lives in an alias list, route via that code. Mirrors
    // backend `resolve_shape_by_alias`.
    let forwardCode = null;
    for (const [rawName, code] of Object.entries(abbrs)) {
      if (String(rawName || "").trim().toLowerCase() === norm) {
        forwardCode = String(code || "").trim().toLowerCase();
        break;
      }
    }
    if (forwardCode) {
      for (const [shape, list] of Object.entries(aliases)) {
        if (!Array.isArray(list)) continue;
        for (const a of list) {
          if (String(a || "").trim().toLowerCase() === forwardCode) return shape;
        }
      }
    }
  }
  // 4. No routing found. Return null so the caller flags this as `pending`
  // rather than silently binding to some other shape's brackets.
  return null;
}
function roundUp(value, nearest) {
  if (!nearest || nearest <= 0) return Math.round(value * 100) / 100;
  return Math.ceil(value / nearest) * nearest;
}

// CS 21.43 -- Per-tier override precedence mirror of backend's
// `_effective_tier_price`. Locked override > Unlocked spot > Computed engine.
// Legacy top-level `selling_override` is treated as an UNLOCKED B2B spot.
function effectiveTierPrice(piece, tier, computed) {
  if (!piece) return computed;
  if (tier !== "b2b" && tier !== "customer") return computed;
  const v = piece[`${tier}_override`];
  if (v !== undefined && v !== null) return Number(v);
  if (tier === "b2b" && piece.selling_override !== undefined && piece.selling_override !== null) {
    return Number(piece.selling_override);
  }
  return computed;
}
function tierOverrideKind(piece, tier) {
  if (!piece) return "none";
  if (tier !== "b2b" && tier !== "customer") return "none";
  const v = piece[`${tier}_override`];
  if (v !== undefined && v !== null) {
    return piece[`${tier}_override_locked`] ? "locked" : "spot";
  }
  if (tier === "b2b" && piece.selling_override !== undefined && piece.selling_override !== null) {
    return "spot";
  }
  return "none";
}
function computeSelling(piece, settings, ratecard, tier = "b2b") {
  // CS 21.11 / Turn F-2 -- tier-aware client-side selling mirror. Reads
  // from settings[tier] subdoc if present, falls back to top-level; diamond
  // lookup uses `sell_{tier}` with a graceful fallback to plain `sell`.
  // CS 21.13 -- tiers are fully independent. Purity chain matches backend
  // compute_selling: tier_cfg.purity_factors (tier's OWN) > cost_purity_factors
  // (HO seed for day-one) > DEFAULT_PURITY. Edits to the tier's purity flow
  // straight through to the live drawer preview.
  const tierCfg = (settings && typeof settings[tier] === "object" && settings[tier])
    ? { ...settings, ...settings[tier] }
    : (settings || {});
  const karat = String(piece.karat || 18);
  const purityMap = (tierCfg.purity_factors && Object.keys(tierCfg.purity_factors).length > 0)
    ? tierCfg.purity_factors
    : ((settings && settings.cost_purity_factors) || DEFAULT_PURITY);
  const purity = Number(purityMap[karat] ?? 0.75);
  const netWt = canonicalWeight(piece.net_wt_g || 0);  // CS 22.12
  const pureGold = netWt * purity;
  const goldRate = Number(tierCfg.gold_rate_per_g || 0);
  const gold = pureGold * goldRate;

  const labourMode = tierCfg.labour_mode || "per_gram";
  const labourRate = Number(tierCfg.labour_rate || 0);
  const labourBase = labourMode === "per_gram" ? netWt * labourRate : labourRate;
  // CS 21.12 -- gold-loss folds into labour: gold_loss_add = pure_gold × gold_rate × loss%
  const goldLossPct = Number(tierCfg.gold_loss_pct || 0) / 100;
  const goldLossAdd = pureGold * goldRate * goldLossPct;
  const labour = labourBase + goldLossAdd;

  const diaWt = Number(piece.dia_wt_ct || 0);
  const lines = piece.diamond_lines || [];
  let diamond = 0;
  if (lines.length) {
    for (const ln of lines) {
      const ln_ct = Number(ln.dia_wt_ct || 0);
      const ln_pcs = Math.max(1, Number(ln.dia_pcs || 0));
      const ln_avg = ln_ct > 0 ? ln_ct / ln_pcs : 0;
      const ln_shape = resolveShape(ratecard, ln.shape || "");
      const rate = ln_ct > 0 ? lookupDiamondSell(ratecard, ln.diamond_table || piece.diamond_table || "standard", ln_shape, ln_avg, tier) : null;
      diamond += (rate == null ? 0 : rate * ln_ct);
    }
  } else {
    const diaPcs = Math.max(1, Number(piece.dia_pcs || 1));
    const avg = diaWt > 0 ? diaWt / diaPcs : 0;
    const table = piece.diamond_table || "standard";
    const shape = resolveShape(ratecard, piece.diamond_shape_group || "Round");
    const rate = diaWt > 0 ? lookupDiamondSell(ratecard, table, shape, avg, tier) : null;
    if (rate == null) {
      const markup = Number(tierCfg.diamond_markup_fallback || settings?.diamond_markup_fallback || 1.4);
      diamond = Number(piece.cost_diamond || 0) * markup;
    } else {
      diamond = rate * diaWt;
    }
  }

  const igiMode = tierCfg.igi_mode || "passthrough";
  const cert =
    igiMode === "passthrough"
      ? Number(piece.cost_igi || 0)
      : igiMode === "fixed"
      ? Number(tierCfg.igi_fixed || 0)
      : igiMode === "per_carat"
      ? Number(tierCfg.igi_per_carat || 0) * diaWt
      : 0;

  const subtotal = gold + labour + diamond + cert;
  const marginPct = Number(tierCfg.margin_value || 0);
  const margin = (tierCfg.margin_mode || "percent") === "percent" ? subtotal * (marginPct / 100) : marginPct;
  const raw = subtotal + margin;
  const nearest = Number((tierCfg.rounding || {}).nearest || (settings?.gold_rate_currency === "INR" ? 100 : 5));
  const selling = roundUp(raw, nearest);
  return {
    gold_sell: gold, labour_sell: labour, labour_base: labourBase, gold_loss_add: goldLossAdd,
    diamond_sell: diamond, cert_sell: cert,
    subtotal, margin, raw, selling_price: selling,
  };
}

// ─── PIN gate ───────────────────────────────────────────────────────
function PinGate({ token, onAuth }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!/^\d{4,8}$/.test(pin)) { setErr("Enter 4-8 digits"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await pub.post(`/w/${token}/auth`, { pin });
      onAuth(pin, data);
    } catch (e) {
      const code = e.response?.status;
      setErr(code === 401 ? "Incorrect PIN" : (e.response?.data?.detail || "Authentication failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D1B2A] p-6" data-testid="wholesaler-portal-gate">
      <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl space-y-5">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#C9A84C]/15 mb-3">
            <Lock className="w-6 h-6 text-[#C9A84C]" />
          </div>
          <h1 className="font-serif text-2xl text-[#0D1B2A]">Wholesaler access</h1>
          <p className="text-sm text-slate-500 mt-1">Enter your 4-8 digit PIN</p>
        </div>
        <Input
          autoFocus
          inputMode="numeric"
          value={pin}
          maxLength={8}
          onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 8)); setErr(""); }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="••••"
          className="text-center font-mono text-2xl tracking-[0.5em] h-14"
          data-testid="wholesaler-portal-pin-input"
        />
        {err && <p className="text-sm text-red-600 text-center" data-testid="wholesaler-portal-pin-error">{err}</p>}
        <Button
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white h-12"
          data-testid="wholesaler-portal-pin-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" /> Unlock</>}
        </Button>
      </div>
    </div>
  );
}

// ─── Selling rates editor (sheet) ───────────────────────────────────
// CS 21.13 -- Two-tab editor. B2B and Customer are fully independent tiers:
// each owns its own gold rate, labour, gold-loss %, purity factors, margin,
// rounding, IGI mode, AND its own diamond bracket sell cells. On first save
// the tier's purity_factors are seeded from HO cost purity by the backend
// (see get_selling), but edits from here never propagate back to cost.

// CS 22.14 -- `endpoints` lets HO reuse this exact sheet for custom wholesale price lists.
export function SellingRatesSheet({ open, onOpenChange, token, pin, settings, onSaved, currency, endpoints }) {
  const ep = endpoints || {
    client: pub, opts: { params: { pin } },
    ratecard: `/w/${token}/diamond-ratecard`, selling: `/w/${token}/selling-settings`,
  };
  const [activeTier, setActiveTier] = useState("b2b");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ratecard, setRatecard] = useState(null);
  const [rcDirty, setRcDirty] = useState(false);
  const [activeShape, setActiveShape] = useState("");
  const sym = currencySymbol(currency);

  const buildDraft = useCallback((s) => {
    if (!s) return null;
    const nearestDefault = currency === "INR" ? 100 : 5;
    const seedPurity = { ...(s.cost_purity_factors || DEFAULT_PURITY) };
    const perTier = (t) => {
      const tv = s[t] || {};
      return {
        gold_rate_per_g: tv.gold_rate_per_g ?? s.gold_rate_per_g ?? 0,
        labour_mode: tv.labour_mode ?? s.labour_mode ?? "per_gram",
        labour_rate: tv.labour_rate ?? s.labour_rate ?? 0,
        gold_loss_pct: tv.gold_loss_pct ?? s.gold_loss_pct ?? 0,
        purity_factors: { ...(tv.purity_factors && Object.keys(tv.purity_factors).length ? tv.purity_factors : seedPurity) },
        igi_mode: tv.igi_mode ?? s.igi_mode ?? "passthrough",
        igi_fixed: tv.igi_fixed ?? s.igi_fixed ?? 0,
        igi_per_carat: tv.igi_per_carat ?? s.igi_per_carat ?? 0,
        margin_mode: tv.margin_mode ?? s.margin_mode ?? "percent",
        margin_value: tv.margin_value ?? s.margin_value ?? 0,
        rounding: { mode: "up", nearest: tv.rounding?.nearest ?? s.rounding?.nearest ?? nearestDefault },
      };
    };
    return {
      selling_rates_locked_in: !!s.selling_rates_locked_in,
      // CS 21.35 -- landed expense: wholesaler-owned, shared across tiers.
      landed_expense_pct: Number(s.landed_expense_pct) || 0,
      b2b: perTier("b2b"),
      customer: perTier("customer"),
    };
  }, [currency]);

  useEffect(() => { setDraft(buildDraft(settings)); }, [settings, buildDraft]);
  useEffect(() => {
    if (!open) return;
    ep.client.get(ep.ratecard, ep.opts)
      .then((r) => {
        setRatecard(r.data);
        setRcDirty(false);
        const shapes = r.data?.standard?.shapes || [];
        if (shapes.length) setActiveShape((prev) => (shapes.includes(prev) ? prev : shapes[0]));
      })
      .catch(() => { /* intentional: rate-card stays on last loaded values; the sheet's own save path reports errors */ });
  }, [open, ep.ratecard]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null;
  const tierDraft = draft[activeTier];
  const upd = (k, v) => setDraft((d) => ({ ...d, [activeTier]: { ...d[activeTier], [k]: v } }));
  const updPurity = (karat, v) => setDraft((d) => ({
    ...d,
    [activeTier]: {
      ...d[activeTier],
      purity_factors: { ...d[activeTier].purity_factors, [karat]: v === "" ? 0 : Number(v) },
    },
  }));

  // Diamond sell writes to the active tier's sell_{tier} field. B2B additionally
  // mirrors into legacy `sell` for backward-compat with pre-F-1 read paths.
  const updSellV2 = (shape, idx, v) => {
    setRatecard((prev) => {
      if (!prev) return prev;
      const bbs = { ...(prev.standard?.brackets_by_shape || {}) };
      const rows = [...(bbs[shape] || [])];
      const nv = v === "" ? null : Number(v);
      const updated = { ...rows[idx], [`sell_${activeTier}`]: nv };
      if (activeTier === "b2b") updated.sell = nv;
      rows[idx] = updated;
      bbs[shape] = rows;
      return { ...prev, standard: { ...prev.standard, brackets_by_shape: bbs } };
    });
    setRcDirty(true);
  };

  const save = async (alsoLock) => {
    setBusy(true);
    try {
      const buildTier = (t) => ({
        gold_rate_per_g: Number(draft[t].gold_rate_per_g) || 0,
        labour_mode: draft[t].labour_mode || "per_gram",
        labour_rate: Number(draft[t].labour_rate) || 0,
        gold_loss_pct: Number(draft[t].gold_loss_pct) || 0,
        purity_factors: Object.fromEntries(
          Object.entries(draft[t].purity_factors || {}).map(([k, v]) => [k, Number(v) || 0])
        ),
        margin_mode: draft[t].margin_mode || "percent",
        margin_value: Number(draft[t].margin_value) || 0,
        rounding: { mode: "up", nearest: Number(draft[t]?.rounding?.nearest) || (currency === "INR" ? 100 : 5) },
        igi_mode: draft[t].igi_mode || "passthrough",
        igi_fixed: Number(draft[t].igi_fixed) || 0,
        igi_per_carat: Number(draft[t].igi_per_carat) || 0,
      });
      const body = {
        b2b: buildTier("b2b"),
        customer: buildTier("customer"),
        // CS 21.35 -- landed expense persisted at top-level (shared across tiers).
        landed_expense_pct: Number(draft.landed_expense_pct) || 0,
      };
      if (alsoLock) body.selling_rates_locked_in = true;
      const [{ data: ssData }, rcRes] = await Promise.all([
        ep.client.put(ep.selling, body, ep.opts),
        rcDirty && ratecard
          ? ep.client.put(ep.ratecard, ratecard, ep.opts)
          : Promise.resolve(null),
      ]);
      onSaved(ssData);
      if (rcRes?.data) { setRatecard(rcRes.data); setRcDirty(false); }
      // CS 21.43 -- surface the override cleanup summary. Sum the counts
      // from both writes so the wholesaler gets a single, unambiguous line.
      const overrideSummaries = [ssData?.overrides, rcRes?.data?.overrides].filter(Boolean);
      const cleared = overrideSummaries.reduce(
        (a, s) => a + (Number(s.b2b_cleared) || 0)
                    + (Number(s.customer_cleared) || 0)
                    + (Number(s.legacy_cleared) || 0),
        0,
      );
      const kept = overrideSummaries.reduce(
        (a, s) => a + (Number(s.b2b_locked_kept) || 0)
                    + (Number(s.customer_locked_kept) || 0),
        0,
      );
      const baseMsg = alsoLock
        ? "Rates saved & locked in — selling prices unlocked."
        : "Rates saved.";
      if (cleared > 0 || kept > 0) {
        toast.success(`${baseMsg} ${cleared} unlocked override${cleared === 1 ? "" : "s"} cleared, ${kept} locked kept.`);
      } else {
        toast.success(baseMsg);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const isLocked = !!draft.selling_rates_locked_in;
  const purityKarats = Array.from(new Set([
    ...Object.keys(tierDraft.purity_factors || {}),
    "9", "14", "18", "950",
  ])).sort((a, b) => Number(a) - Number(b));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white max-w-6xl w-[95vw] h-[92vh] sm:h-[88vh] p-0 flex flex-col overflow-hidden"
        data-testid="wholesaler-rates-sheet"
      >
        <DialogHeader className="px-6 sm:px-8 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-serif text-2xl sm:text-3xl flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#C9A84C]" /> {endpoints ? "Selling Rates (B2B used in catalogs)" : "My Selling Rates"}
          </DialogTitle>
          {!isLocked && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
              Selling prices are hidden until you save &amp; lock in your rates.
            </p>
          )}
          <div className="mt-4 flex gap-2" data-testid="rate-tier-tabs">
            {[
              { id: "b2b", label: "B2B" },
              { id: "customer", label: "Customer" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTier(t.id)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  activeTier === t.id
                    ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`rate-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* CS 21.35 -- Landed expense: cost-side lever, shared across tiers.
              Positioned OUTSIDE the tier tab body because it applies to cost
              regardless of which tier is being viewed. */}
          <div className="mt-4 rounded-md border border-[#C9A84C]/30 bg-[#FAF8F3] px-4 py-3"
               data-testid="landed-expense-block">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[#0D1B2A]">
                  Landed Expense %
                  <span className="ml-2 text-[10px] text-slate-500 font-normal">shipping · duty · on-cost</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Adds to your Cost only; does not change B2B or Customer prices.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number" step="0.01" min="0"
                  value={draft.landed_expense_pct ?? 0}
                  onChange={(e) => setDraft((d) => ({ ...d, landed_expense_pct: e.target.value === "" ? 0 : Number(e.target.value) }))}
                  className="font-mono text-right w-24"
                  data-testid="landed-expense-input"
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-8" data-testid={`rate-tier-body-${activeTier}`}>
          <section>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-3">
              Pricing levers · {activeTier === "b2b" ? "B2B" : "Customer"}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              <Field label={`Gold rate (${sym}/gram)`} testid={`rate-${activeTier}-gold`}>
                <CurrencyInput sym={sym} step="0.0001" value={tierDraft.gold_rate_per_g ?? 0}
                  onChange={(v) => upd("gold_rate_per_g", v)} testid={`rate-${activeTier}-gold-input`} />
              </Field>
              <Field label="Labour mode" testid={`rate-${activeTier}-labour-mode`}>
                <Select value={tierDraft.labour_mode || "per_gram"} onValueChange={(v) => upd("labour_mode", v)}>
                  <SelectTrigger data-testid={`rate-${activeTier}-labour-mode-trigger`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_gram">per gram</SelectItem>
                    <SelectItem value="per_piece">per piece</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Labour rate (${sym})`} testid={`rate-${activeTier}-labour`}>
                <CurrencyInput sym={sym} step="0.01" value={tierDraft.labour_rate ?? 0}
                  onChange={(v) => upd("labour_rate", v)} testid={`rate-${activeTier}-labour-input`} />
              </Field>
              <Field label="Gold loss % (folds into labour)" testid={`rate-${activeTier}-gold-loss`}>
                <Input type="number" step="0.01" min="0" max="100"
                  value={tierDraft.gold_loss_pct ?? 0}
                  onChange={(e) => upd("gold_loss_pct", e.target.value)}
                  className="font-mono text-right" data-testid={`rate-${activeTier}-gold-loss-input`} />
              </Field>
              <Field label="Margin % (optional uplift on subtotal)" testid={`rate-${activeTier}-margin`}>
                <Input type="number" step="0.1" value={tierDraft.margin_value ?? 0}
                  onChange={(e) => upd("margin_value", e.target.value)}
                  className="font-mono text-right" data-testid={`rate-${activeTier}-margin-input`} />
              </Field>
              <Field label={`Round up to nearest (${sym})`} testid={`rate-${activeTier}-rounding`}>
                <Input type="number" step="1"
                  value={tierDraft?.rounding?.nearest ?? (currency === "INR" ? 100 : 5)}
                  onChange={(e) => upd("rounding", { ...(tierDraft.rounding || {}), nearest: e.target.value })}
                  className="font-mono text-right" data-testid={`rate-${activeTier}-rounding-input`} />
              </Field>
              <Field label="IGI / cert mode" testid={`rate-${activeTier}-igi-mode`}>
                <Select value={tierDraft.igi_mode || "passthrough"} onValueChange={(v) => upd("igi_mode", v)}>
                  <SelectTrigger data-testid={`rate-${activeTier}-igi-mode-trigger`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passthrough">passthrough (use piece cert cost)</SelectItem>
                    <SelectItem value="fixed">per piece (fixed)</SelectItem>
                    <SelectItem value="per_carat">per carat ({sym}/ct × dia carats)</SelectItem>
                    <SelectItem value="none">none</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {tierDraft.igi_mode === "fixed" && (
                <Field label={`IGI fixed (${sym} / piece)`} testid={`rate-${activeTier}-igi-fixed`}>
                  <CurrencyInput sym={sym} step="0.01" value={tierDraft.igi_fixed ?? 0}
                    onChange={(v) => upd("igi_fixed", v)} testid={`rate-${activeTier}-igi-fixed-input`} />
                </Field>
              )}
              {tierDraft.igi_mode === "per_carat" && (
                <Field label={`IGI rate (${sym} / carat)`} testid={`rate-${activeTier}-igi-per-carat`}>
                  <CurrencyInput sym={sym} step="0.01" value={tierDraft.igi_per_carat ?? 0}
                    onChange={(v) => upd("igi_per_carat", v)} testid={`rate-${activeTier}-igi-per-carat-input`} />
                </Field>
              )}
            </div>
          </section>

          {/* CS 21.13 -- Per-tier purity factors (pure-gold conversion). */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-2">
              Purity factors · {activeTier === "b2b" ? "B2B" : "Customer"} (pure-gold conversion)
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Independent per tier. Seeded from HO cost purity on first save; edits do not propagate back to cost or the other tier.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`rate-${activeTier}-purity-grid`}>
              {purityKarats.map((k) => (
                <Field key={k} label={k === "950" ? "PT950" : `${k}KT`} testid={`rate-${activeTier}-purity-${k}`}>
                  <Input type="number" step="0.001" min="0" max="1"
                    value={tierDraft.purity_factors?.[k] ?? ""}
                    onChange={(e) => updPurity(k, e.target.value)}
                    className="font-mono text-right"
                    data-testid={`rate-${activeTier}-purity-${k}-input`} />
                </Field>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">
                  Diamond rate-card · {activeTier === "b2b" ? "B2B" : "Customer"}
                </div>
                <div className="text-sm text-slate-500">{sym} per carat · cost locked by HO · you set sell per shape/bracket</div>
              </div>
            </div>
            {!ratecard ? (
              <div className="text-xs text-slate-400">Loading rate-card…</div>
            ) : (
              (() => {
                const bbs = ratecard.standard?.brackets_by_shape || {};
                const shapes = ratecard.standard?.shapes || Object.keys(bbs);
                const currentShape = shapes.includes(activeShape) ? activeShape : (shapes[0] || null);
                if (!currentShape) return <div className="text-xs text-slate-400">No shape columns configured yet — ask HO to add shapes.</div>;
                const sellField = `sell_${activeTier}`;
                return (
                  <>
                    <div className="mb-3 flex gap-1.5 overflow-x-auto pb-2" data-testid="portal-diamond-tabs">
                      {shapes.map((sg) => (
                        <button
                          key={sg}
                          type="button"
                          onClick={() => setActiveShape(sg)}
                          className={`shrink-0 text-xs px-3 py-2 rounded-full border ${
                            currentShape === sg
                              ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                              : "bg-white text-slate-600 border-slate-200"
                          }`}
                          data-testid={`portal-diamond-tab-${sg.replace(/[^A-Za-z0-9]/g, "_")}`}
                        >
                          {sg}
                        </button>
                      ))}
                    </div>
                    {/* CS 21.35 -- Per-shape "Markup % on cost" for the active
                        tier. Wholesaler-owned. Fills every bracket in this
                        shape with sell = round(cost × (1 + pct/100)). Skips
                        zero-cost brackets and reports them clearly so a HO
                        gap in cost cells looks like a HO gap, not a bug. */}
                    <div className="mb-3 rounded-md border border-[#C9A84C]/30 bg-[#FAF8F3] px-3 py-2 flex items-center gap-2 flex-wrap"
                         data-testid={`portal-shape-markup-${currentShape}`}>
                      <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">
                        Markup on {activeTier === "b2b" ? "B2B" : "Customer"} cost
                      </span>
                      <Input
                        type="number" step="1" min="0"
                        value={ratecard?.standard?.shape_markups?.[activeTier]?.[currentShape] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRatecard((prev) => {
                            if (!prev) return prev;
                            const std = { ...(prev.standard || {}) };
                            const markups = { ...(std.shape_markups || {}) };
                            const tierMap = { ...(markups[activeTier] || {}) };
                            tierMap[currentShape] = v === "" ? "" : Number(v);
                            markups[activeTier] = tierMap;
                            std.shape_markups = markups;
                            return { ...prev, standard: std };
                          });
                          setRcDirty(true);
                        }}
                        placeholder="%"
                        className="font-mono text-right h-8 w-20"
                        data-testid={`portal-shape-markup-input-${currentShape}`}
                      />
                      <span className="text-xs text-slate-500">%</span>
                      <button
                        type="button"
                        onClick={() => {
                          const pctRaw = ratecard?.standard?.shape_markups?.[activeTier]?.[currentShape];
                          const pct = Number(pctRaw);
                          if (!Number.isFinite(pct) || pct <= 0) {
                            toast.error("Enter a positive markup % (e.g. 30 for 30%)");
                            return;
                          }
                          const rows = ratecard?.standard?.brackets_by_shape?.[currentShape] || [];
                          if (rows.length === 0) { toast.error(`No brackets in ${currentShape}`); return; }
                          const field = `sell_${activeTier}`;
                          const zeroCostCount = rows.filter((r) => !(Number(r.cost) > 0)).length;
                          const applyCount = rows.length - zeroCostCount;
                          if (applyCount === 0) {
                            toast.error(`${zeroCostCount} of ${rows.length} brackets skipped — HO hasn't set a cost for ${currentShape} yet.`);
                            return;
                          }
                          const existingCount = rows.filter((r) => (Number(r[field]) || 0) > 0).length;
                          const tierLabel = activeTier === "b2b" ? "B2B" : "Customer";
                          if (existingCount > 0) {
                            const ok = window.confirm(
                              `This will overwrite ${existingCount} existing ${tierLabel} rate${existingCount === 1 ? "" : "s"} in ${currentShape}. Continue?`
                            );
                            if (!ok) return;
                          }
                          const factor = 1 + pct / 100;
                          const newRows = rows.map((row) => {
                            const cost = Number(row.cost) || 0;
                            if (cost <= 0) return row;
                            const marked = Math.round(cost * factor);
                            const nextRow = { ...row, [field]: marked };
                            if (activeTier === "b2b") nextRow.sell = marked;
                            return nextRow;
                          });
                          setRatecard((prev) => {
                            if (!prev) return prev;
                            const bbs = { ...(prev.standard?.brackets_by_shape || {}) };
                            bbs[currentShape] = newRows;
                            return { ...prev, standard: { ...prev.standard, brackets_by_shape: bbs } };
                          });
                          setRcDirty(true);
                          toast.success(
                            `${currentShape} ${tierLabel}: filled ${applyCount} bracket${applyCount === 1 ? "" : "s"} at cost × ${factor.toFixed(2)}` +
                            (zeroCostCount > 0
                              ? ` · ${zeroCostCount} of ${rows.length} skipped — HO hasn't set a cost for these yet`
                              : "")
                          );
                        }}
                        className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/85"
                        data-testid={`portal-shape-markup-apply-${currentShape}`}
                      >
                        Apply
                      </button>
                      <span className="text-[10px] text-slate-400 ml-auto">
                        fills every {currentShape} bracket · undoable until Save
                      </span>
                    </div>
                    <div className="rounded-md border border-[#0D1B2A]/10 overflow-hidden" data-testid={`portal-diamond-grid-${currentShape}`}>
                      <table className="w-full text-sm">
                        <thead className="bg-[#0D1B2A]/5">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium min-w-[110px]">Bracket (ct)</th>
                            <th className="px-3 py-2 text-right font-medium font-mono text-[10px] text-slate-500 min-w-[100px]">cost</th>
                            <th className="px-3 py-2 text-right font-medium font-mono text-[10px] text-slate-500 min-w-[130px]">
                              sell · {activeTier === "b2b" ? "B2B" : "Customer"}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(bbs[currentShape] || []).map((row, idx) => {
                            const sellVal = row[sellField] ?? row.sell ?? "";
                            return (
                              <tr key={`${currentShape}-${idx}`} className="border-t border-[#0D1B2A]/5">
                                <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">{row.range}</td>
                                <td className="px-3 py-2 text-right font-mono text-slate-500 bg-slate-50/70"
                                    data-testid={`portal-dia-${currentShape}-${idx}-cost`}>
                                  {row.cost == null ? "—" : fmtMoney(row.cost, currency)}
                                </td>
                                <td className="px-2 py-1">
                                  <Input
                                    type="number" inputMode="decimal"
                                    value={sellVal ?? ""}
                                    onChange={(e) => updSellV2(currentShape, idx, e.target.value)}
                                    className="text-right font-mono h-9 px-2"
                                    placeholder={sym}
                                    data-testid={`portal-dia-${currentShape}-${idx}-sell-${activeTier}`}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                          {(bbs[currentShape] || []).length === 0 && (
                            <tr>
                              <td colSpan={3} className="px-3 py-4 text-center text-slate-400 text-xs">
                                No brackets configured on this shape.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()
            )}
          </section>
        </div>

        <div className="shrink-0 border-t border-slate-100 px-6 sm:px-8 py-4 bg-white flex justify-end gap-2 pb-[max(env(safe-area-inset-bottom),1rem)]">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
          {!isLocked ? (
            <Button
              onClick={() => save(true)}
              disabled={busy}
              className="h-11 bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white px-6"
              data-testid="rate-save-lock"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Save &amp; Lock-in</>}
            </Button>
          ) : (
            <Button
              onClick={() => save(false)}
              disabled={busy}
              className="h-11 bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white px-6"
              data-testid="rate-save"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save rates"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CurrencyInput({ sym, value, onChange, step, testid }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-mono pointer-events-none">{sym}</span>
      <Input
        type="number" step={step}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className="pl-7 font-mono text-right"
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─── Piece detail drawer ────────────────────────────────────────────
const STATUS_LABELS = {
  in_stock: "In stock",
  sold: "Sold",
  memo: "Memo (on hold)",
  returned: "Returned",
};

function PieceDetail({ piece, settings, ratecard, currency, locked, token, pin, recipientName, visibility, onChanged, onClose }) {
  const b2bLive = useMemo(
    () => (piece ? computeSelling(piece, settings || {}, ratecard, "b2b") : null),
    [piece, settings, ratecard]
  );
  const custLive = useMemo(
    () => (piece ? computeSelling(piece, settings || {}, ratecard, "customer") : null),
    [piece, settings, ratecard]
  );
  const live = custLive || b2bLive; // legacy alias for existing consumers

  // CS 21.9 / Turn D-1 -- Per-line diamond breakup for the drawer table.
  // Strictly binds to the wholesaler's OWN rate-card (both cost & sell cells).
  // Never reads from head-office / franchise pricing.
  const diaLines = useMemo(() => {
    if (!piece) return [];
    const lines = piece.diamond_lines || [];
    const abbrOf = buildAbbrResolver(ratecard, lines);
    return lines.map((ln) => {
      const raw_shape = ln.shape || "";
      const dia_pcs = Number(ln.dia_pcs || 0);
      const dia_wt_ct = Number(ln.dia_wt_ct || 0);
      const avg = dia_pcs > 0 && dia_wt_ct > 0 ? dia_wt_ct / dia_pcs : 0;
      const resolved = resolveShape(ratecard, raw_shape);
      const table = ln.diamond_table || piece.diamond_table || "standard";
      const costHit = dia_wt_ct > 0 ? lookupDiamondRate(ratecard, table, resolved, avg, "cost") : { rate: null, bracket: null };
      // Turn F-2 Phase 2 -- resolve BOTH tier sells (with legacy fallback).
      let b2bHit = dia_wt_ct > 0 ? lookupDiamondRate(ratecard, table, resolved, avg, "sell_b2b") : { rate: null, bracket: null };
      if (b2bHit.rate == null && dia_wt_ct > 0) b2bHit = lookupDiamondRate(ratecard, table, resolved, avg, "sell");
      let custHit = dia_wt_ct > 0 ? lookupDiamondRate(ratecard, table, resolved, avg, "sell_customer") : { rate: null, bracket: null };
      if (custHit.rate == null && dia_wt_ct > 0) custHit = lookupDiamondRate(ratecard, table, resolved, avg, "sell");
      const bracket = costHit.bracket?.range || b2bHit.bracket?.range || custHit.bracket?.range || "";
      const pending = !raw_shape || resolved == null;
      // CS 21.15 -- shape resolved but NO bracket covers this size -> flag it.
      const no_bracket_for_size = !pending && dia_wt_ct > 0 && costHit.bracket == null && b2bHit.bracket == null && custHit.bracket == null;
      return {
        raw_shape,
        abbr: abbrOf(raw_shape),
        resolved_shape: resolved,
        dia_pcs,
        dia_wt_ct,
        avg,
        bracket,
        cost_rate: costHit.rate,
        b2b_rate: b2bHit.rate,
        sell_rate: custHit.rate,
        cost_amount: costHit.rate != null ? costHit.rate * dia_wt_ct : null,
        b2b_amount: b2bHit.rate != null ? b2bHit.rate * dia_wt_ct : null,
        sell_amount: custHit.rate != null ? custHit.rate * dia_wt_ct : null,
        pending,
        no_bracket_for_size,
      };
    });
  }, [piece, ratecard]);

  const noBracketLines = useMemo(
    () => diaLines.filter((l) => l.no_bracket_for_size),
    [diaLines],
  );

  const diaPcsTotal = useMemo(
    () => diaLines.reduce((s, l) => s + (Number(l.dia_pcs) || 0), 0),
    [diaLines]
  );
  const diaCtTotal = useMemo(
    () => diaLines.reduce((s, l) => s + (Number(l.dia_wt_ct) || 0), 0),
    [diaLines]
  );

  const [statusDialog, setStatusDialog] = useState(null); // 'sold'|'memo'|'returned'|'in_stock'|null
  const [realised, setRealised] = useState("");
  const [saleDate, setSaleDate] = useState("");
  const [soldClient, setSoldClient] = useState("");
  const [memoClient, setMemoClient] = useState("");
  const [memoDate, setMemoDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // CS 21.10 -- Turn E: collapsible Diamonds section, defaults to collapsed
  // summary; expand state persists for the drawer session.
  const [diaExpanded, setDiaExpanded] = useState(false);
  // CS 21.10 -- Turn E: image upload state (drawer edit-photo button).
  // Turn F-0 -- second ref for camera-direct input (mobile snap-from-phone).
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // CS 21.43 -- Per-tier manual price override drafts. Reset whenever the
  // piece changes so we don't accidentally reuse another piece's draft.
  const [b2bOvDraft, setB2bOvDraft] = useState("");
  const [custOvDraft, setCustOvDraft] = useState("");
  const [b2bOvLock, setB2bOvLock] = useState(false);
  const [custOvLock, setCustOvLock] = useState(false);
  const [savingOverride, setSavingOverride] = useState(null); // 'b2b' | 'customer' | null
  // CS 21.52 -- per-piece Description / Note with public toggle.
  const DESC_MAX = 200;
  const [descDraft, setDescDraft] = useState("");
  const [descPublic, setDescPublic] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  useEffect(() => {
    if (!piece) return;
    setDescDraft(String(piece.description || ""));
    setDescPublic(!!piece.description_public);
  }, [piece?.jewel_code, piece?.description, piece?.description_public]);
  useEffect(() => {
    if (!piece) return;
    const b = piece.b2b_override ?? piece.selling_override ?? null;
    const c = piece.customer_override ?? null;
    setB2bOvDraft(b === null || b === undefined ? "" : String(b));
    setCustOvDraft(c === null || c === undefined ? "" : String(c));
    setB2bOvLock(!!(piece.b2b_override_locked ?? piece.override_locked));
    setCustOvLock(!!piece.customer_override_locked);
  }, [piece?.jewel_code, piece?.b2b_override, piece?.customer_override,
      piece?.b2b_override_locked, piece?.customer_override_locked,
      piece?.selling_override, piece?.override_locked]);

  if (!piece) return null;
  const status = piece.stock_status || "in_stock";

  const openStatus = (next) => {
    // Sold flow snapshots the B2B tier (that's the buyer paying us).
    const sellNow = effectiveTierPrice(piece, "b2b", b2bLive?.selling_price) ?? 0;
    const today = new Date().toISOString().slice(0, 10);
    setRealised(String(sellNow || ""));
    setMemoClient(piece.memo_client_name || "");
    setMemoDate((piece.memo_date || "").slice(0, 10) || today);
    setSoldClient((piece.sale_snapshot?.sold_client_name) || "");
    setSaleDate((piece.sold_date || piece.sale_snapshot?.sold_at || "").slice(0, 10) || today);
    setStatusDialog(next);
  };

  const confirmStatus = async () => {
    if (statusDialog === "sold") {
      if (!soldClient.trim()) return toast.error("Client name is required to mark sold");
      if (!saleDate) return toast.error("Sale date is required");
      if (realised === "" || Number.isNaN(Number(realised))) return toast.error("Realised price is required");
    }
    if (statusDialog === "memo") {
      if (!memoClient.trim()) return toast.error("Client name is required for memo");
      if (!memoDate) return toast.error("Memo date is required");
    }
    setBusy(true);
    try {
      const body = { stock_status: statusDialog };
      if (statusDialog === "sold") {
        body.sold_price = Number(realised);
        body.sold_client_name = soldClient.trim();
        body.sold_date = new Date(saleDate).toISOString();
      }
      if (statusDialog === "memo") {
        body.memo_client_name = memoClient.trim();
        body.memo_date = new Date(memoDate).toISOString();
      }
      await pub.put(`/w/${token}/stock/${piece.jewel_code}/status`, body, { params: { pin } });
      toast.success(`${piece.jewel_code} → ${STATUS_LABELS[statusDialog] || statusDialog}`);
      setStatusDialog(null);
      // Turn F-0 UX fix -- keep the drawer OPEN after a status change so the
      // user sees the just-applied state; parent's onChanged re-fetches the
      // stock list AND the piece itself (via onPieceChanged below).
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // CS 21.10 -- Turn E #3. Wholesaler-initiated photo upload from drawer.
  // Turn F-0 -- adds a Camera-direct button (mobile snap-from-phone) that
  // fires the same upload handler via a separate hidden input with
  // `capture="environment"`. On desktop both buttons open the file picker.
  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await pub.post(`/w/${token}/stock/${piece.jewel_code}/photo`, fd, {
        params: { pin },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  // CS 21.13 -- Delete/Remove image. Clears image_url on THIS piece (photo
  // bytes remain in storage, may be re-attached later). Confirm-guarded.
  const handlePhotoDelete = async () => {
    if (!piece?.image_url) return;
    if (!window.confirm("Remove this piece's photo? It will revert to the placeholder.")) return;
    setUploadingPhoto(true);
    try {
      await pub.delete(`/w/${token}/stock/${piece.jewel_code}/photo`, { params: { pin } });
      toast.success("Photo removed");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remove failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // CS 21.43 -- Per-tier override save + clear. Precedence enforced server-side:
  // Locked > Unlocked (spot) > Computed. Sends both tiers on every save so a
  // partial edit doesn't accidentally wipe the other tier's stored value.
  const saveOverride = async (tier) => {
    if (!locked) return toast.error("Lock in your selling rates first");
    const draft = tier === "b2b" ? b2bOvDraft : custOvDraft;
    const lockFlag = tier === "b2b" ? b2bOvLock : custOvLock;
    const raw = String(draft).trim();
    if (raw !== "" && !Number.isFinite(Number(raw))) {
      return toast.error("Enter a valid number");
    }
    const val = raw === "" ? null : Number(raw);
    setSavingOverride(tier);
    try {
      const body = {
        [`${tier}_override`]: val,
        [`${tier}_override_locked`]: val === null ? false : !!lockFlag,
      };
      await pub.put(`/w/${token}/stock/${piece.jewel_code}/override`, body, { params: { pin } });
      const label = tier === "b2b" ? "B2B" : "Customer";
      if (val === null) {
        toast.success(`${label} override cleared`);
      } else if (lockFlag) {
        toast.success(`${label} override saved · Locked (survives rate changes)`);
      } else {
        toast.success(`${label} spot price saved · updates with rates`);
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message
        || err.response?.data?.detail
        || "Override save failed");
    } finally {
      setSavingOverride(null);
    }
  };
  const clearOverride = async (tier) => {
    if (tier === "b2b") { setB2bOvDraft(""); setB2bOvLock(false); }
    else { setCustOvDraft(""); setCustOvLock(false); }
    setSavingOverride(tier);
    try {
      await pub.put(`/w/${token}/stock/${piece.jewel_code}/override`, {
        [`${tier}_override`]: null,
        [`${tier}_override_locked`]: false,
      }, { params: { pin } });
      toast.success(`${tier === "b2b" ? "B2B" : "Customer"} override cleared`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Clear failed");
    } finally {
      setSavingOverride(null);
    }
  };

  // CS 21.52 -- Save the per-piece description + public/private toggle.
  // Server enforces trim + 200-char cap + private-by-default projection;
  // the frontend is a thin caller. Empty text clears both fields (server
  // forces `description_public=false` on clear so a future re-open doesn't
  // surface a stale toggle).
  const saveDescription = async () => {
    const trimmed = String(descDraft || "").trim();
    if (trimmed.length > DESC_MAX) {
      return toast.error(`Description must be ${DESC_MAX} characters or less`);
    }
    setSavingDesc(true);
    try {
      const body = {
        description: trimmed ? trimmed : null,
        description_public: trimmed ? !!descPublic : false,
      };
      await pub.put(
        `/w/${token}/stock/${piece.jewel_code}/description`,
        body,
        { params: { pin } },
      );
      if (!trimmed) {
        toast.success("Description cleared");
      } else if (descPublic) {
        toast.success("Description saved · visible to customers");
      } else {
        toast.success("Description saved · private (only you see it)");
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message
        || err.response?.data?.detail
        || "Save failed");
    } finally {
      setSavingDesc(false);
    }
  };

  if (!piece) return null;
  const systemSell = effectiveTierPrice(piece, "customer", custLive?.selling_price) ?? 0;
  const b2bEffective = effectiveTierPrice(piece, "b2b", b2bLive?.selling_price) ?? 0;
  const custEffective = systemSell;
  const b2bKind = tierOverrideKind(piece, "b2b");
  const custKind = tierOverrideKind(piece, "customer");
  const margin = (systemSell || 0) - (piece.cost_total || 0);
  const isSold = piece.stock_status === "sold";
  // CS 21.33 -- subtotalCost includes landed expense so the drawer's Cost
  // column ties out to piece.cost_total (which the backend now computes as
  // product_cost + expense).
  const subtotalCost = (Number(piece.cost_gold) || 0)
    + (Number(piece.cost_labour) || 0)
    + (Number(piece.cost_diamond) || 0)
    + (Number(piece.cost_igi) || 0)
    + (Number(piece.cost_expense) || 0);
  // CS 21.10 -- Turn E #4. Days-on-memo counter for the drawer header stat.
  const memoDays = (piece.stock_status === "memo" && piece.memo_date)
    ? Math.max(0, Math.floor((Date.now() - new Date(piece.memo_date).getTime()) / 86400000))
    : null;

  return (
    <Sheet open={!!piece} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl lg:max-w-4xl overflow-y-auto bg-[#FAF8F3] border-l-[#0D1B2A]/10 pb-[max(env(safe-area-inset-bottom),5rem)]"
        data-testid="piece-detail"
      >
        <SheetHeader className="text-left">
          <div className="flex items-start justify-between gap-3 pr-10 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">
                {[piece.category, piece.metal].filter(Boolean).join(" · ") || "Wholesaler"}
              </div>
              <SheetTitle className="font-serif text-3xl text-[#0D1B2A] mt-1">
                {piece.jewel_code}
              </SheetTitle>
              <div className="text-sm text-slate-500 mt-0.5">{piece.style_no || "—"}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusPill status={status} />
              {status === "memo" && piece.memo_client_name && (
                <span className="text-[11px] text-slate-600 bg-amber-100/70 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="memo-client-detail">
                  out to <span className="font-medium">{piece.memo_client_name}</span>
                </span>
              )}
              {memoDays !== null && (
                <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      data-testid="memo-aging-drawer">
                  <Clock className="w-3 h-3" /> on memo {memoDays} day{memoDays === 1 ? "" : "s"}
                </span>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Image + Spec strip */}
        <div className="mt-6 grid gap-5 md:grid-cols-[220px_1fr]">
          <div className="relative group" data-testid="piece-image-wrap">
            <PieceImage
              src={piece.image_url}
              alt={piece.jewel_code}
              className="w-full aspect-square rounded-md border border-[#0D1B2A]/8 bg-white"
              testid={`piece-detail-image-${piece.jewel_code}`}
            />
            {/* CS 21.10 Turn E #3 -- inline photo replace.
                Turn F-0 -- adds a separate "camera direct" input so mobile
                users can snap-from-phone straight into the piece. */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoPick}
              data-testid="piece-photo-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handlePhotoPick}
              data-testid="piece-photo-camera-input"
            />
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="rounded-full bg-white/95 border border-[#0D1B2A]/10 shadow-sm p-1.5 opacity-90 hover:opacity-100 hover:bg-white transition disabled:opacity-40"
                data-testid="piece-photo-camera"
                title="Snap photo from camera"
              >
                {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0D1B2A]" /> : <Camera className="w-3.5 h-3.5 text-[#0D1B2A]" />}
              </button>
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="rounded-full bg-white/95 border border-[#0D1B2A]/10 shadow-sm p-1.5 opacity-90 hover:opacity-100 hover:bg-white transition disabled:opacity-40"
                data-testid="piece-photo-edit"
                title={piece.image_url ? "Replace photo from file" : "Upload photo from file"}
              >
                {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0D1B2A]" /> : <Pencil className="w-3.5 h-3.5 text-[#0D1B2A]" />}
              </button>
              {piece.image_url && (
                <button
                  type="button"
                  onClick={handlePhotoDelete}
                  disabled={uploadingPhoto}
                  className="rounded-full bg-white/95 border border-red-200 shadow-sm p-1.5 opacity-90 hover:opacity-100 hover:bg-red-50 transition disabled:opacity-40"
                  data-testid="piece-photo-delete"
                  title="Remove photo"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </button>
              )}
            </div>
          </div>
          <div className="luxe-card p-5" data-testid="piece-spec-strip">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Specs</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3 text-sm">
              <MetaCell label="Style" value={piece.style_no || "—"} />
              <MetaCell label="Metal" value={piece.metal || `${piece.karat || 18}KT`} />
              <MetaCell label="Category" value={piece.category || "—"} />
              <MetaCell label="Net Weight" value={`${formatWeight(piece.net_wt_g || 0)} g`} />
              <MetaCell label="Gross Weight" value={piece.gross_wt_g ? `${Number(piece.gross_wt_g).toFixed(3)} g` : "—"} />
              <MetaCell label="Diamonds" value={(diaPcsTotal > 0 || diaCtTotal > 0) ? `${diaPcsTotal} pcs · ${diaCtTotal.toFixed(3)} ct` : "—"} />
            </div>
          </div>
        </div>

        {/* CS 21.52 -- Per-piece Description / Note.
            Compact editable field with a "Show to customers" toggle. Private
            by default (toggle OFF). Private notes stay inside the portal and
            never reach retailer catalog, share PDF, or Excel exports (the
            server-side projection strips them). Public notes appear on the
            retailer shopfront, share PDF cards, and Excel exports. */}
        <div className="mt-6 luxe-card p-5" data-testid="piece-description-card">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">
                Description / Note
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                {descPublic
                  ? "Visible to your customers on shopfront, PDF, and Excel."
                  : "Private — only you can see this."}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] uppercase tracking-[0.15em] text-slate-500">
                Show to customers
              </span>
              <Switch
                checked={!!descPublic}
                onCheckedChange={(v) => setDescPublic(!!v)}
                data-testid="piece-description-public-toggle"
                aria-label="Show description to customers"
              />
            </div>
          </div>
          <textarea
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value.slice(0, DESC_MAX))}
            maxLength={DESC_MAX}
            rows={2}
            placeholder="e.g. Bestseller in December. Comes with matching earrings on request."
            className="mt-3 w-full text-sm resize-none rounded-md border border-[#0D1B2A]/12 bg-white/70 focus:bg-white focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]/40 outline-none px-3 py-2 leading-relaxed"
            data-testid="piece-description-input"
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              className={`text-[11px] tabular-nums ${
                descDraft.length >= DESC_MAX - 20 ? "text-amber-600" : "text-slate-400"
              }`}
              data-testid="piece-description-counter"
            >
              {descDraft.length}/{DESC_MAX}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={savingDesc}
              onClick={saveDescription}
              data-testid="piece-description-save"
              className="border-[#0D1B2A]/20 text-[#0D1B2A] hover:bg-[#0D1B2A]/5"
            >
              {savingDesc ? "Saving…" : "Save note"}
            </Button>
          </div>
        </div>

        {/* Cost & Sell Analysis */}
        <div className="mt-6 luxe-card p-0 overflow-hidden">
          <div className="p-5 pb-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Pricing Breakdown</p>
            <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">Cost &amp; Sell Analysis</h3>
            <p className="text-[11px] text-slate-400 mt-1">Your paid cost vs your selling math — bound strictly to this wholesaler&rsquo;s rates.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[580px]" data-testid="wholesaler-pricing-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/8">
                  <th className="px-5 py-3 text-left font-medium">Component</th>
                  {(visibility?.cost ?? true) && <th className="px-5 py-3 text-right font-medium" data-testid="col-cost">Cost</th>}
                  {(visibility?.b2b ?? true) && <th className="px-5 py-3 text-right font-medium bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8" data-testid="col-b2b">B2B</th>}
                  {(visibility?.sell ?? true) && <th className="px-5 py-3 text-right font-medium bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" data-testid="col-sell">Sell</th>}
                </tr>
              </thead>
              <tbody className="font-sans font-semibold tabular-nums">
                <ThreeLaneRow label="Gold" costV={piece.cost_gold} b2bV={b2bLive?.gold_sell} sellV={custLive?.gold_sell} locked={locked} currency={currency} visibility={visibility} testid="row-gold" />
                <ThreeLaneRow label="Labour" costV={piece.cost_labour} b2bV={b2bLive?.labour_sell} sellV={custLive?.labour_sell} locked={locked} currency={currency} visibility={visibility} testid="row-labour" />
                <ThreeLaneRow label="Diamond" costV={piece.cost_diamond} b2bV={b2bLive?.diamond_sell} sellV={custLive?.diamond_sell} locked={locked} currency={currency} visibility={visibility} testid="row-diamond" />
                <ThreeLaneRow label="Cert" costV={piece.cost_igi} b2bV={b2bLive?.cert_sell} sellV={custLive?.cert_sell} locked={locked} currency={currency} visibility={visibility} testid="row-cert" />

                {/* CS 21.33 -- Landed expense row. Cost column only; B2B/Customer
                    columns show em-dash because expense is a cost-side lever
                    that never uplifts selling. Hidden when expense is zero to
                    avoid clutter for wholesalers who don't use it. */}
                {(Number(piece.cost_expense) || 0) > 0 && (
                  <tr className="border-t border-[#0D1B2A]/5" data-testid="row-expense">
                    <td className="px-5 py-3 text-slate-600 font-normal">
                      Expense
                      {piece.cost_expense_pct != null && (
                        <span className="ml-2 text-[10px] text-slate-400">
                          ({Number(piece.cost_expense_pct).toFixed(2)}% shipping · duty)
                        </span>
                      )}
                    </td>
                    {(visibility?.cost ?? true) && (
                      <td className="px-5 py-3 text-right" data-testid="cost-expense-cell">
                        {fmtMoney(piece.cost_expense, currency)}
                      </td>
                    )}
                    {(visibility?.b2b ?? true) && (
                      <td className="px-5 py-3 text-right text-slate-300 bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8">—</td>
                    )}
                    {(visibility?.sell ?? true) && (
                      <td className="px-5 py-3 text-right text-slate-300 bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">—</td>
                    )}
                  </tr>
                )}

                <tr className="border-t border-[#0D1B2A]/8">
                  <td className="px-5 py-3 text-slate-600 font-normal">Sub-total</td>
                  {(visibility?.cost ?? true) && <td className="px-5 py-3 text-right">{fmtMoney(subtotalCost, currency)}</td>}
                  {(visibility?.b2b ?? true) && (
                    <td className="px-5 py-3 text-right bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8">
                      {locked ? fmtMoney(b2bLive?.subtotal, currency) : <span className="text-slate-300">—</span>}
                    </td>
                  )}
                  {(visibility?.sell ?? true) && (
                    <td className="px-5 py-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">
                      {locked ? fmtMoney(custLive?.subtotal, currency) : <span className="text-slate-300">—</span>}
                    </td>
                  )}
                </tr>

                <tr className="border-t-2 border-[#C9A84C]/50 bg-[#0D1B2A]/3">
                  <td className="px-5 py-4 font-serif text-[#0D1B2A] uppercase tracking-wider text-xs">Total</td>
                  {(visibility?.cost ?? true) && (
                    <td className="px-5 py-4 text-right font-sans font-bold text-lg text-[#0D1B2A]" data-testid="cost-total">
                      {fmtMoney(piece.cost_total, currency)}
                    </td>
                  )}
                  {(visibility?.b2b ?? true) && (
                    <td className="px-5 py-4 text-right font-sans font-bold text-lg text-[#0D1B2A] bg-[#0D1B2A]/5 border-l border-[#0D1B2A]/8" data-testid="b2b-total">
                      {locked ? (
                        <>
                          {fmtMoney(b2bEffective, currency)}
                          {b2bKind !== "none" && (
                            <div className="text-[10px] text-amber-600 font-normal italic mt-0.5"
                                 data-testid="b2b-override-badge">
                              {b2bKind === "locked" ? "Manual · Locked" : "Manual · updates with rates"}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-amber-700 flex items-center gap-1 justify-end font-normal">
                          <Lock className="w-3 h-3" /> set rates
                        </span>
                      )}
                    </td>
                  )}
                  {(visibility?.sell ?? true) && (
                    <td className="px-5 py-4 text-right font-sans font-bold text-lg text-[#0D1B2A] bg-[#C9A84C]/10 border-l-2 border-[#C9A84C]" data-testid="sell-total">
                      {locked ? (
                        <>
                          {fmtMoney(custEffective, currency)}
                          {custKind !== "none" && (
                            <div className="text-[10px] text-amber-600 font-normal italic mt-0.5"
                                 data-testid="cust-override-badge">
                              {custKind === "locked" ? "Manual · Locked" : "Manual · updates with rates"}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-amber-700 flex items-center gap-1 justify-end font-normal">
                          <Lock className="w-3 h-3" /> set rates
                        </span>
                      )}
                    </td>
                  )}
                </tr>

                {locked && (visibility?.sell ?? true) && (
                  <tr className="border-t border-[#0D1B2A]/8">
                    <td className="px-5 py-3 text-slate-600 font-normal">Margin (Sell − Cost)</td>
                    {(visibility?.cost ?? true) && <td className="px-5 py-3 text-right text-slate-300">—</td>}
                    {(visibility?.b2b ?? true) && <td className="px-5 py-3 text-right text-slate-300 bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8">—</td>}
                    <td className="px-5 py-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" data-testid="piece-margin">
                      <span className="text-[#C9A84C]">{fmtMoney((custEffective || 0) - (piece.cost_total || 0), currency)}</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* CS 21.43 -- Manual pricing overrides (B2B + Customer, fully independent).
            Locked = survives rate-card changes / re-seeds.
            Unlocked = spot price, wiped on any bulk rate-card mutation. */}
        {locked && (
          <div className="mt-6 luxe-card p-5" data-testid="manual-pricing-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Manual Pricing</p>
                <h3 className="font-serif text-2xl text-[#0D1B2A] mt-1">Override this piece</h3>
                <p className="text-[11px] text-slate-500 mt-1">
                  Lock to keep a premium price steady across rate changes. Leave unlocked for a spot price that&rsquo;ll clear on the next rate-card update.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {["b2b", "customer"].map((tier) => {
                const label = tier === "b2b" ? "B2B" : "Customer";
                const draft = tier === "b2b" ? b2bOvDraft : custOvDraft;
                const setDraft = tier === "b2b" ? setB2bOvDraft : setCustOvDraft;
                const lockFlag = tier === "b2b" ? b2bOvLock : custOvLock;
                const setLock = tier === "b2b" ? setB2bOvLock : setCustOvLock;
                const kind = tierOverrideKind(piece, tier);
                const currentSaved = tier === "b2b"
                  ? (piece.b2b_override ?? piece.selling_override)
                  : piece.customer_override;
                const computed = (tier === "b2b" ? b2bLive : custLive)?.selling_price;
                return (
                  <div key={tier}
                       className={`rounded-md border p-4 ${kind === "locked" ? "border-[#0D1B2A]/30 bg-[#0D1B2A]/3" : kind === "spot" ? "border-amber-300 bg-amber-50/40" : "border-slate-200 bg-white"}`}
                       data-testid={`override-tier-${tier}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Engine: {computed != null ? fmtMoney(computed, currency) : "—"}
                        </div>
                      </div>
                      {kind !== "none" && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${kind === "locked" ? "bg-[#0D1B2A]/10 border-[#0D1B2A]/30 text-[#0D1B2A]" : "bg-amber-100/80 border-amber-300 text-amber-800"}`}
                              data-testid={`override-badge-${tier}`}>
                          {kind === "locked" ? "Manual · Locked" : "Manual · Spot"}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2 items-center">
                      <span className="text-slate-400 text-sm">{currencySymbol(currency)}</span>
                      <Input
                        type="number" step="0.01" min="0"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={computed != null ? String(computed) : "Enter price"}
                        className="font-mono text-right"
                        data-testid={`override-input-${tier}`}
                      />
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
                           data-testid={`override-lock-label-${tier}`}>
                      <Switch checked={lockFlag}
                              onCheckedChange={(v) => setLock(!!v)}
                              data-testid={`override-lock-${tier}`} />
                      <span>Lock — survives rate-card changes</span>
                    </label>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveOverride(tier)}
                        disabled={savingOverride === tier}
                        className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white flex-1"
                        data-testid={`override-save-${tier}`}
                      >
                        {savingOverride === tier ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                      </Button>
                      {(currentSaved !== null && currentSaved !== undefined) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => clearOverride(tier)}
                          disabled={savingOverride === tier}
                          data-testid={`override-clear-${tier}`}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Diamonds -- collapsible (CS 21.10 Turn E #1): default collapsed
            summary, expand to reveal the per-line table. */}
        {diaLines.length > 0 && (
          <div className="mt-6 luxe-card p-0 overflow-hidden" data-testid="wholesaler-diamonds-section">
            <button
              type="button"
              onClick={() => setDiaExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#FAF8F3] transition-colors"
              data-testid="diamonds-toggle"
              aria-expanded={diaExpanded}
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Diamonds</p>
                <div className="font-serif text-xl text-[#0D1B2A] mt-0.5 tabular-nums">
                  {diaPcsTotal} pcs · {diaCtTotal.toFixed(3)} ct
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <span className="text-[11px] uppercase tracking-wider">{diaExpanded ? "Hide" : "Break-up"}</span>
                {diaExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
            {diaExpanded && (
              <div className="border-t border-[#0D1B2A]/8 px-5 pb-5 pt-3">
                <p className="text-[11px] text-slate-400 mb-3">
                  Shape abbreviations seeded by HO. Rates read only from your own rate-card cells.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm tabular-nums" data-testid="wholesaler-diamonds-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-[#0D1B2A]/8">
                        <th className="py-3 pr-3 font-medium">#</th>
                        <th className="py-3 pr-3 font-medium">Shape</th>
                        <th className="py-3 pr-3 text-right font-medium">Pcs</th>
                        <th className="py-3 pr-3 text-right font-medium">Total Wt</th>
                        <th className="py-3 pr-3 text-right font-medium">Avg</th>
                        <th className="py-3 pr-3 font-medium">Bracket</th>
                        {/* CS 21.44 -- Three tiers (Cost / B2B / Sell) each get
                            a Rate + Amount pair, and are gated by the same
                            visibility toggles as the rest of the drawer. */}
                        {(visibility?.cost ?? true) && (
                          <>
                            <th className="py-3 pr-3 text-right font-medium" data-testid="dia-th-cost-rate">Cost Rate</th>
                            <th className="py-3 pr-3 text-right font-medium" data-testid="dia-th-cost-amt">Cost</th>
                          </>
                        )}
                        {(visibility?.b2b ?? true) && (
                          <>
                            <th className="py-3 pr-3 text-right font-medium bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8" data-testid="dia-th-b2b-rate">B2B Rate</th>
                            <th className="py-3 pr-3 text-right font-medium bg-[#0D1B2A]/3" data-testid="dia-th-b2b-amt">B2B</th>
                          </>
                        )}
                        {(visibility?.sell ?? true) && (
                          <>
                            <th className="py-3 pr-3 text-right font-medium bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" data-testid="dia-th-sell-rate">Sell Rate</th>
                            <th className="py-3 text-right font-medium bg-[#C9A84C]/5" data-testid="dia-th-sell-amt">Sell</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="font-semibold">
                      {diaLines.map((ln, i) => (
                        <tr key={`${ln.raw_shape || ln.shape || "line"}-${i}`}
                            className={`border-t border-[#0D1B2A]/5 ${ln.pending ? "bg-amber-50/40" : ln.no_bracket_for_size ? "bg-red-50/40" : ""}`}
                            data-testid={`dia-line-${i}`}>
                          <td className="py-3 pr-3 text-slate-400 font-normal">{i + 1}</td>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span
                                className="inline-flex items-center justify-center min-w-[2rem] px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider rounded bg-[#0D1B2A]/5 text-[#0D1B2A] border border-[#0D1B2A]/10"
                                data-testid={`dia-abbr-${i}`}
                                title={ln.raw_shape}
                              >
                                {ln.abbr}
                              </span>
                              <span className="text-xs text-slate-500 font-normal">{ln.raw_shape || "—"}</span>
                              {ln.no_bracket_for_size && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                                  data-testid={`dia-no-bracket-${i}`}
                                  title={`Shape ${ln.resolved_shape} has no brackets defined — add one to price this stone`}
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  no brackets defined
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-3 text-right">{ln.dia_pcs}</td>
                          <td className="py-3 pr-3 text-right">{ln.dia_wt_ct.toFixed(3)} ct</td>
                          <td className="py-3 pr-3 text-right text-slate-500 font-normal">{ln.avg > 0 ? ln.avg.toFixed(4) : "—"}</td>
                          <td className="py-3 pr-3 text-xs text-slate-500 font-normal">{ln.bracket || "—"}</td>
                          {(visibility?.cost ?? true) && (
                            <>
                              <td className="py-3 pr-3 text-right" data-testid={`dia-cost-rate-${i}`}>
                                {ln.cost_rate != null ? fmtMoney(ln.cost_rate, currency) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="py-3 pr-3 text-right" data-testid={`dia-cost-amt-${i}`}>
                                {ln.cost_amount != null ? fmtMoney(ln.cost_amount, currency) : <span className="text-slate-300">—</span>}
                              </td>
                            </>
                          )}
                          {(visibility?.b2b ?? true) && (
                            <>
                              <td className="py-3 pr-3 text-right bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8" data-testid={`dia-b2b-rate-${i}`}>
                                {locked && ln.b2b_rate != null ? fmtMoney(ln.b2b_rate, currency) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="py-3 pr-3 text-right bg-[#0D1B2A]/3" data-testid={`dia-b2b-amt-${i}`}>
                                {locked && ln.b2b_amount != null ? fmtMoney(ln.b2b_amount, currency) : <span className="text-slate-300">—</span>}
                              </td>
                            </>
                          )}
                          {(visibility?.sell ?? true) && (
                            <>
                              <td className="py-3 pr-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]" data-testid={`dia-sell-rate-${i}`}>
                                {locked && ln.sell_rate != null ? fmtMoney(ln.sell_rate, currency) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="py-3 text-right bg-[#C9A84C]/5" data-testid={`dia-sell-amt-${i}`}>
                                {locked && ln.sell_amount != null ? fmtMoney(ln.sell_amount, currency) : <span className="text-slate-300">—</span>}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sale snapshot */}
        {isSold && piece.sale_snapshot && (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="piece-snapshot">
            <div className="font-medium text-slate-800 mb-1">Sale snapshot · frozen</div>
            <div className="grid grid-cols-2 gap-y-1">
              <span>Sold at</span><span className="font-mono">{(piece.sale_snapshot.sold_at || "").slice(0, 19).replace("T", " ")}</span>
              {piece.sale_snapshot.sold_client_name && (
                <>
                  <span>Client</span>
                  <span className="font-mono" data-testid="snapshot-client">{piece.sale_snapshot.sold_client_name}</span>
                </>
              )}
              <span>Realised</span><span className="font-mono">{fmtMoney(piece.sale_snapshot.realised_price ?? piece.sale_snapshot.system_selling_price, currency)}</span>
              <span>Cost at sale</span><span className="font-mono">{fmtMoney(piece.sale_snapshot.cost_total, currency)}</span>
              <span>Margin at sale</span><span className="font-mono">{fmtMoney(piece.sale_snapshot.margin, currency)}</span>
            </div>
          </div>
        )}

        {/* Status switcher — every state is freely changeable */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Status</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="status-switcher">
            <StatusButton
              testid="status-in_stock"
              active={status === "in_stock"}
              onClick={() => openStatus("in_stock")}
              disabled={status === "in_stock"}
              icon={<Tag className="w-4 h-4" />}
              label="In stock"
            />
            <StatusButton
              testid="status-sold"
              active={status === "sold"}
              onClick={() => openStatus("sold")}
              disabled={!locked && status !== "sold"}
              hint={!locked && status !== "sold" ? "Lock in your selling rates first" : ""}
              icon={<ShoppingBag className="w-4 h-4" />}
              label="Mark sold"
            />
            <StatusButton
              testid="status-memo"
              active={status === "memo"}
              onClick={() => openStatus("memo")}
              icon={<Tag className="w-4 h-4" />}
              label="Mark memo"
            />
            <StatusButton
              testid="status-returned"
              active={status === "returned"}
              onClick={() => openStatus("returned")}
              icon={<Tag className="w-4 h-4" />}
              label="Mark returned"
            />
          </div>
          <Button
            onClick={() => setShareOpen(true)}
            variant="outline"
            className="mt-3 w-full h-11"
            data-testid="piece-share"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share this piece
          </Button>
        </div>
      </SheetContent>

      {/* Status dialog (sold / memo / returned / in_stock) */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => { if (!o) setStatusDialog(null); }}>
        <DialogContent className="bg-white" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {statusDialog === "sold" && `Mark ${piece.jewel_code} sold`}
              {statusDialog === "memo" && `Mark ${piece.jewel_code} as memo`}
              {statusDialog === "returned" && `Mark ${piece.jewel_code} returned`}
              {statusDialog === "in_stock" && `Bring ${piece.jewel_code} back to stock`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {statusDialog === "sold" && (
              <>
                <p className="text-sm text-slate-500">
                  Freezes your current cost, system selling price &amp; margin into a snapshot. Recompute-cost
                  &amp; rate-card changes won&rsquo;t move them after.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                    Client name *
                  </label>
                  <Input
                    value={soldClient}
                    onChange={(e) => setSoldClient(e.target.value)}
                    placeholder="e.g. Anita Sharma"
                    className="h-12"
                    data-testid="sold-client-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                      Sale date *
                    </label>
                    <Input
                      type="date"
                      value={saleDate}
                      onChange={(e) => setSaleDate(e.target.value)}
                      className="h-12"
                      data-testid="sold-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                      Realised price ({currency}) *
                    </label>
                    <Input
                      type="number" step="0.01"
                      value={realised}
                      onChange={(e) => setRealised(e.target.value)}
                      className="font-mono text-right h-12"
                      data-testid="sold-realised-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-slate-400">
                  Defaults to your system selling price ({fmtMoney(systemSell, currency)}). Edit if the realised amount differs.
                </p>
              </>
            )}
            {statusDialog === "memo" && (
              <>
                <p className="text-sm text-slate-500">
                  Memo means the piece is out to a customer but not yet sold. No snapshot is written — the
                  margin stays live and re-prices with your rate changes.
                </p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                    Client name *
                  </label>
                  <Input
                    value={memoClient}
                    onChange={(e) => setMemoClient(e.target.value)}
                    placeholder="e.g. Anita Sharma"
                    className="h-12"
                    data-testid="memo-client-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                    Memo date *
                  </label>
                  <Input
                    type="date"
                    value={memoDate}
                    onChange={(e) => setMemoDate(e.target.value)}
                    className="h-12"
                    data-testid="memo-date-input"
                  />
                </div>
              </>
            )}
            {statusDialog === "returned" && (
              <p className="text-sm text-slate-500">
                Marks the piece returned to inventory. Any existing sale snapshot is archived
                (you keep it in the history) but cleared from the live row.
              </p>
            )}
            {statusDialog === "in_stock" && (
              <p className="text-sm text-slate-500">
                Brings the piece back to active stock. If it was previously sold, the snapshot is archived.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)} disabled={busy}>Cancel</Button>
            <Button onClick={confirmStatus} disabled={busy} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="status-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single-piece share dialog */}
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        token={token}
        pin={pin}
        selected={piece ? [piece.jewel_code] : []}
        recipientName={recipientName || "Catalogue"}
      />
    </Sheet>
  );
}
function Panel({ title, children, tone = "cost", testid }) {
  const colour = tone === "cost" ? "border-slate-200 bg-slate-50/70" : "border-[#C9A84C]/30 bg-[#FAF8F3]";
  return (
    <div className={`rounded-md border ${colour} p-3 text-sm space-y-1`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

// CS 21.9 / Turn D-1 -- meta cell in the drawer's spec strip.
function MetaCell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm text-[#0D1B2A] mt-1 truncate font-medium">{value}</div>
    </div>
  );
}

// CS 21.11 / Turn F-2 -- Cost | B2B | Sell three-lane row for the drawer's
// Pricing Breakdown table. Each column is independently gated by visibility.
function ThreeLaneRow({ label, costV, b2bV, sellV, locked, currency, visibility, testid }) {
  return (
    <tr className="border-t border-[#0D1B2A]/5 first:border-t-0" data-testid={testid}>
      <td className="px-5 py-3 text-slate-600 font-normal">{label}</td>
      {(visibility?.cost ?? true) && (
        <td className="px-5 py-3 text-right">{fmtMoney(costV, currency)}</td>
      )}
      {(visibility?.b2b ?? true) && (
        <td className="px-5 py-3 text-right bg-[#0D1B2A]/3 border-l border-[#0D1B2A]/8">
          {locked ? fmtMoney(b2bV, currency) : <span className="text-slate-300">—</span>}
        </td>
      )}
      {(visibility?.sell ?? true) && (
        <td className="px-5 py-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">
          {locked ? fmtMoney(sellV, currency) : <span className="text-slate-300">—</span>}
        </td>
      )}
    </tr>
  );
}

// CS 21.12 gold-loss bugfix -- labour_base + gold_loss_add remain on the
// computeSelling return dict so the drawer TOTAL is correct and any future
// UI can consume the breakdown. CS 21.13 -- the two-line "base + gold-loss"
// subline was collapsed to net total only per user spec.

// CS 21.9 / Turn D-1 -- Cost vs Selling two-lane row for the drawer's
// Pricing Breakdown table. Selling column shows "—" when the wholesaler
// hasn't locked in their rates yet.
function TwoLaneRow({ label, costV, sellV, locked, currency, testid }) {
  return (
    <tr className="border-t border-[#0D1B2A]/5 first:border-t-0" data-testid={testid}>
      <td className="px-5 py-3 text-slate-600 font-normal">{label}</td>
      <td className="px-5 py-3 text-right">{fmtMoney(costV, currency)}</td>
      <td className="px-5 py-3 text-right bg-[#C9A84C]/5 border-l-2 border-[#C9A84C]">
        {locked ? fmtMoney(sellV, currency) : <span className="text-slate-300">—</span>}
      </td>
    </tr>
  );
}

// CS 21.5 — status pill (colour-coded), used in piece detail + cards
function StatusPill({ status }) {
  const map = {
    in_stock: { label: "In stock", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    sold: { label: "Sold", cls: "bg-slate-800 text-white border-slate-800" },
    memo: { label: "Memo", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    returned: { label: "Returned", cls: "bg-slate-100 text-slate-600 border-slate-300" },
    // CS 21.42b -- designs are made-to-order, NOT in stock. Distinct red pill
    // mirrors the red bottom-right dot on the image.
    design: { label: "Made to order", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const m = map[status] || map.in_stock;
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.cls}`}>
      {m.label}
    </span>
  );
}

// CS 21.5 — status switcher button
function StatusButton({ testid, active, onClick, disabled, icon, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint || ""}
      data-testid={testid}
      className={`h-12 rounded-md border flex items-center justify-center gap-2 text-sm transition-colors ${
        active
          ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
          : disabled
          ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
          : "bg-white text-[#0D1B2A] border-slate-200 hover:border-[#C9A84C] hover:bg-[#FAF8F3]"
      }`}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}
function Row({ label, v, currency, bold = false, tone = "slate" }) {
  const valueColor = tone === "amber" ? "text-amber-700" : "text-[#0D1B2A]";
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500 text-xs">{label}</span>
      <span className={`font-mono ${bold ? "font-semibold" : ""} ${valueColor}`}>{fmtMoney(v, currency)}</span>
    </div>
  );
}

// ─── Photo helper — falls back to a placeholder when no image_url ──
function PieceImage({ src, alt, className = "", testid }) {
  const [err, setErr] = useState(false);
  const url = src && !err
    ? (src.startsWith("/api/") ? `${BACKEND}${src}` : src)
    : null;
  if (!url) {
    return (
      <div
        className={`flex items-center justify-center bg-gradient-to-br from-[#FAF8F3] to-slate-100 text-slate-300 ${className}`}
        data-testid={testid}
      >
        <ImageIcon className="w-1/3 h-1/3" strokeWidth={1.2} />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      onError={() => setErr(true)}
      className={`object-cover ${className}`}
      data-testid={testid}
      loading="lazy"
    />
  );
}

// ─── CS 21.70 -- PIN-gated delete confirmation dialog ────────────────
function DeleteConfirmDialog({ open, onOpenChange, token, pin, jewelCode, onDeleted }) {
  const [deletePin, setDeletePin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [lockedUntil, setLockedUntil] = useState(null);

  useEffect(() => {
    if (open) { setDeletePin(""); setErr(""); setRemaining(null); setLockedUntil(null); }
  }, [open]);

  const submit = async () => {
    if (!/^\d{4}$/.test(deletePin)) { setErr("Delete PIN must be exactly 4 digits"); return; }
    setBusy(true); setErr("");
    try {
      await pub.post(`/w/${token}/stock/${encodeURIComponent(jewelCode)}/delete`,
                     { access_pin: pin, delete_pin: deletePin });
      toast.success(`Deleted ${jewelCode}`);
      onOpenChange(false);
      onDeleted?.(jewelCode);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 423) {
        const until = detail?.locked_until ? new Date(detail.locked_until) : null;
        setLockedUntil(until);
        setErr(until
          ? `Too many wrong attempts. Try again after ${until.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`
          : "Locked. Please try again later.");
      } else if (status === 401 && typeof detail === "object") {
        setRemaining(detail.remaining_attempts ?? null);
        setErr(`Incorrect delete PIN. ${detail.remaining_attempts ?? 0} attempt(s) left.`);
      } else if (status === 403) {
        setErr("Delete is not enabled for this account. Contact Ashth HO.");
      } else {
        setErr(typeof detail === "string" ? detail : "Delete failed");
      }
    } finally { setBusy(false); }
  };

  const isLocked = lockedUntil && lockedUntil > new Date();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onOpenChange(false); }}>
      <DialogContent className="bg-white" data-testid="delete-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-red-700 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Delete piece
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-600 space-y-3">
          <p>
            You&apos;re about to delete <span className="font-mono text-[#0D1B2A]">{jewelCode}</span>{" "}
            from your live stock. It will vanish from your grid, retailer links,
            and any share exports. Ashth HO can restore it from Trash.
          </p>
          <p className="text-xs text-slate-500">
            Enter the 4-digit <b>delete PIN</b> your Ashth contact gave you.
            (Different from the access PIN you used to log in.) 5 wrong tries
            → 10-minute lockout.
          </p>
        </div>
        <Input
          autoFocus
          inputMode="numeric"
          maxLength={4}
          disabled={isLocked}
          value={deletePin}
          onChange={(e) => { setDeletePin(e.target.value.replace(/\D/g, "").slice(0, 4)); setErr(""); }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="0000"
          className="text-center font-mono text-2xl tracking-[0.5em] h-14"
          data-testid="delete-confirm-pin-input"
        />
        {err && (
          <p className="text-xs text-red-600 text-center" data-testid="delete-confirm-error">{err}</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || isLocked || !/^\d{4}$/.test(deletePin)}
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
            data-testid="delete-confirm-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : "Confirm Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Share dialog — customer-facing document ────────────────────────
function ShareDialog({ open, onOpenChange, token, pin, selected, recipientName }) {
  const [withPrice, setWithPrice] = useState(true);
  // CS 21.30 -- tier picker mirrors distributor share flow. Wholesaler
  // audience is {b2b, customer}. Cost is NEVER an option, on any path.
  const AVAILABLE_TIERS = [
    { key: "b2b", label: "B2B Price" },
    { key: "customer", label: "Customer Sell" },
  ];
  const [selectedTiers, setSelectedTiers] = useState(
    () => new Set(AVAILABLE_TIERS.map((t) => t.key)),
  );
  const toggleTier = (k) => setSelectedTiers((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const count = selected.length;

  const submit = async () => {
    if (count === 0) return toast.error("Select at least one piece");
    if (withPrice && selectedTiers.size === 0) {
      return toast.error("Pick at least one price tier or turn prices off");
    }
    setBusy(true);
    try {
      // CS 22.11 -- PDF only, no piece cap; batched server render merged into one PDF.
      const { blob } = await runSharePdfJob({
        client: pub, base: `/w/${token}`, params: { pin },
        createBody: {
          jewel_codes: selected,
          with_price: withPrice,
          selected_tiers: withPrice ? Array.from(selectedTiers) : null,
        },
        onProgress: (done, total) => setProgress({ done, total }),
      });
      downloadBlob(blob, `${recipientName.replace(/\s+/g, "_")}_catalogue.pdf`);
      toast.success("Downloaded.");
      onOpenChange(false);
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.error === "selling_rates_not_locked") {
        toast.error("Save & lock your selling rates before sharing prices.");
      } else {
        toast.error(await shareErrorMessage(e));
      }
    } finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#C9A84C]" /> Share to customer
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-slate-500" data-testid="share-count-text">
          Sharing <span className="font-semibold text-[#0D1B2A]">{count}</span> piece{count === 1 ? "" : "s"}.
          Cost is never included — B2B and Customer Sell are the only tiers available.
        </DialogDescription>
        <div className="space-y-4 mt-2">
          <label className="flex items-center justify-between p-3 rounded-md border border-slate-200">
            <div>
              <div className="text-sm font-medium">Include prices</div>
              <div className="text-[11px] text-slate-500">Off ⇒ photo + specs only · On ⇒ choose tiers below</div>
            </div>
            <Switch checked={withPrice} onCheckedChange={setWithPrice} data-testid="share-with-price" />
          </label>
          {withPrice && (
            <div className="p-3 rounded-md border border-slate-200" data-testid="share-tier-picker">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Which price tiers?</div>
              <div className="space-y-2">
                {AVAILABLE_TIERS.map((t) => {
                  const checked = selectedTiers.has(t.key);
                  const isLast = checked && selectedTiers.size === 1;
                  return (
                    <label key={t.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTier(t.key)}
                        disabled={isLast}
                        className="w-4 h-4 accent-[#C9A84C]"
                        data-testid={`share-tier-${t.key}`}
                      />
                      <span className="text-sm">{t.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                Uncheck to hide a tier. Cost is never shown on any card or PDF.
              </p>
            </div>
          )}
          <div className="text-[11px] text-slate-500" data-testid="share-format">Format: PDF (one card per page)</div>
          {busy && progress && (
            <div className="space-y-1.5" data-testid="share-progress">
              <div className="text-xs text-[#0D1B2A]">Preparing PDF, {progress.done} of {progress.total}</div>
              <div className="h-1.5 bg-[#0D1B2A]/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#C9A84C] transition-[width] duration-300" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="share-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || count === 0 || (withPrice && selectedTiers.size === 0)}
            className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
            data-testid="share-submit"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {progress ? `${progress.done} of ${progress.total}` : `Preparing ${count}…`}</>
              : <><Download className="w-4 h-4 mr-2" /> Download</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── CS 21.38 — Retailer Links management modal ─────────────────────
function RetailerLinksModal({ open, onOpenChange, token, pin }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ retailer_label: "", password: "", show_b2b: true, show_mrp: true });
  const [showPw, setShowPw] = useState({});
  const publicBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api\/?$/, "");

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { data } = await pub.get(`/w/${token}/retailer-links`, { params: { pin } });
      setLinks(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load retailer links");
    } finally { setLoading(false); }
  }, [open, token, pin]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const label = form.retailer_label.trim();
    if (!label) { toast.error("Retailer label is required"); return; }
    if ((form.password || "").length < 4) { toast.error("Password must be at least 4 characters"); return; }
    setCreating(true);
    try {
      await pub.post(`/w/${token}/retailer-links`, {
        retailer_label: label, password: form.password,
        show_b2b: form.show_b2b, show_mrp: form.show_mrp,
      }, { params: { pin } });
      toast.success(`Link created for ${label}`);
      setForm({ retailer_label: "", password: "", show_b2b: true, show_mrp: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create link");
    } finally { setCreating(false); }
  };

  const patch = async (rlid, body, okMsg) => {
    try {
      await pub.patch(`/w/${token}/retailer-links/${rlid}`, body, { params: { pin } });
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const del = async (rlid, label) => {
    if (!window.confirm(`Delete the link for "${label}"? Retailer will lose access immediately.`)) return;
    try {
      await pub.delete(`/w/${token}/retailer-links/${rlid}`, { params: { pin } });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const copyUrl = (linkToken, label) => {
    const url = `${publicBase}/wr/${linkToken}`;
    navigator.clipboard?.writeText(url);
    toast.success(`Copied ${label}'s link URL`);
  };

  const rotatePassword = async (rlid, label) => {
    const pw = window.prompt(`New password for ${label} (min 4 chars):`);
    if (!pw) return;
    if (pw.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    patch(rlid, { password: pw }, "Password rotated");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl w-[95vw] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="retailer-links-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#C9A84C]" /> Retailer Links
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Wholesaler-created, password-protected shopfronts. Toggle B2B / Suggested MRP visibility per link.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Create */}
          <div className="rounded-md border border-[#C9A84C]/30 bg-[#FAF8F3] p-4 space-y-3" data-testid="rl-create-block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C]">Create new</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Retailer label (e.g. Aventura Fine Jewels)"
                value={form.retailer_label}
                onChange={(e) => setForm((f) => ({ ...f, retailer_label: e.target.value }))}
                data-testid="rl-create-label" />
              <Input placeholder="Password (min 4 chars)" type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                data-testid="rl-create-password" />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.show_b2b} onCheckedChange={(v) => setForm((f) => ({ ...f, show_b2b: v }))}
                        data-testid="rl-create-show-b2b" />
                Show B2B Price
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.show_mrp} onCheckedChange={(v) => setForm((f) => ({ ...f, show_mrp: v }))}
                        data-testid="rl-create-show-mrp" />
                Show Suggested MRP
              </label>
              <Button onClick={create} disabled={creating}
                className="ml-auto bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="rl-create-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Create link</>}
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-3" data-testid="rl-list">
            {loading && <div className="text-sm text-slate-400 text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
            {!loading && links.length === 0 && (
              <div className="text-sm text-slate-400 text-center py-8">No retailer links yet. Create one above.</div>
            )}
            {links.map((l) => {
              const url = `${publicBase}/wr/${l.token}`;
              return (
                <div key={l.id} className={`rounded-md border p-4 space-y-2 ${l.is_active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`}
                     data-testid={`rl-row-${l.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif text-lg text-[#0D1B2A] truncate">{l.retailer_label}</div>
                      <a href={url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-slate-500 hover:text-[#C9A84C] break-all">
                        {url}
                      </a>
                    </div>
                    {!l.is_active && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">Revoked</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 pt-1">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Switch checked={!!l.show_b2b} onCheckedChange={(v) => patch(l.id, { show_b2b: v }, `B2B ${v ? "shown" : "hidden"}`)}
                              data-testid={`rl-toggle-b2b-${l.id}`} />
                      B2B Price
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Switch checked={!!l.show_mrp} onCheckedChange={(v) => patch(l.id, { show_mrp: v }, `MRP ${v ? "shown" : "hidden"}`)}
                              data-testid={`rl-toggle-mrp-${l.id}`} />
                      Suggested MRP
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Switch checked={!!l.is_active} onCheckedChange={(v) => patch(l.id, { is_active: v }, v ? "Restored" : "Revoked")}
                              data-testid={`rl-toggle-active-${l.id}`} />
                      Active
                    </label>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button variant="outline" size="sm" onClick={() => copyUrl(l.token, l.retailer_label)} data-testid={`rl-copy-${l.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => rotatePassword(l.id, l.retailer_label)} data-testid={`rl-rotate-pw-${l.id}`}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => patch(l.id, { regenerate_token: true }, "New URL generated")} data-testid={`rl-rotate-token-${l.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => del(l.id, l.retailer_label)} className="text-red-600 hover:text-red-700" data-testid={`rl-delete-${l.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// ─── Main portal page ───────────────────────────────────────────────
export default function WholesalerPortal() {
  const { token } = useParams();
  const [pin, setPin] = useState(null);
  const [meta, setMeta] = useState(null);
  const [stock, setStock] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ratecard, setRatecard] = useState(null);
  const [search, setSearch] = useState("");
  // CS 21.42 fix -- default status view = "available" (in_stock ∪ design)
  // so newly-assigned designs surface immediately alongside real stock.
  const [statusFilter, setStatusFilter] = useState("available");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activePiece, setActivePiece] = useState(null);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [rlOpen, setRlOpen] = useState(false);
  // CS 21.70 -- PIN-gated soft-delete target (jewel_code) for the confirm modal.
  const [deleteTarget, setDeleteTarget] = useState(null);
  // CS 21.39 -- sort + shape filter (client-side; portal has full tier
  // visibility governed by the existing Cost/B2B/Sell toggles, so no
  // gating needed here).
  const [sort, setSort] = useState("");
  const [shapeSel, setShapeSel] = useState(() => new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const toggleShape = (s) => setShapeSel((prev) => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });
  // CS 21.11 / Turn F-2 -- three independent visibility toggles (Cost / B2B / Sell)
  // replacing the single Live-Preview switch. Persisted per-token.
  const [visibility, setVisibility] = useState(() => {
    try {
      const raw = localStorage.getItem(`wp_vis_${token}`);
      if (raw) return { cost: true, b2b: true, sell: true, ...JSON.parse(raw) };
    } catch (err) {
      // Safari private mode / corrupt JSON — fall through to default.
      console.warn("[wp] visibility read failed; using defaults.", err);
    }
    return { cost: true, b2b: true, sell: true };
  });
  useEffect(() => {
    try { localStorage.setItem(`wp_vis_${token}`, JSON.stringify(visibility)); }
    catch (err) {
      // Safari private mode / quota — non-blocking; toggle won't persist.
      console.warn("[wp] visibility write failed; state kept in memory.", err);
    }
  }, [visibility, token]);
  const toggleVis = (k) => setVisibility((v) => ({ ...v, [k]: !v[k] }));

  const refresh = useCallback(async () => {
    if (!pin) return;
    try {
      const [stockR, ssR, rcR] = await Promise.all([
        pub.get(`/w/${token}/stock`, {
          params: {
            pin,
            status: statusFilter,
            category: categoryFilter === "all" ? undefined : categoryFilter,
            search: search || undefined,
          },
        }),
        pub.get(`/w/${token}/selling-settings`, { params: { pin } }),
        pub.get(`/w/${token}/diamond-ratecard`, { params: { pin } }),
      ]);
      setStock(stockR.data);
      setSettings(ssR.data);
      setRatecard(rcR.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    }
  }, [token, pin, statusFilter, categoryFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  // CS 21.16 -- on drawer open, refetch the rate-card so a bracket HO added
  // upstream after this session started immediately re-resolves diamond
  // lines. Piece data doesn't change client-side, but the rate-card can, so
  // this is the "on next view" contract from the user's spec.
  useEffect(() => {
    if (!activePiece || !pin) return;
    pub.get(`/w/${token}/diamond-ratecard`, { params: { pin } })
      .then((r) => setRatecard(r.data))
      .catch(() => { /* keep prior rate-card on failure */ });
  }, [activePiece, token, pin]);

  if (!pin) return <PinGate token={token} onAuth={(p, m) => { setPin(p); setMeta(m); }} />;
  if (!stock || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F3]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9A84C]" />
      </div>
    );
  }

  const currency = meta?.cost_currency || "USD";
  const locked = !!stock.selling_rates_locked_in;
  const rawItems = stock.items || [];
  const categoryOptions = Array.from(new Set(rawItems.map((p) => p.category).filter(Boolean))).sort();
  // Shapes present across the (currently loaded) stock — for shape filter.
  const shapeOptions = Array.from(new Set(
    rawItems.flatMap((p) => (p.diamond_lines || []).map((ln) => ln.shape).filter(Boolean))
  )).sort();
  // Apply shape filter + sort client-side. Portal has full tier visibility,
  // so all price sort options are available.
  const items = (() => {
    let out = rawItems;
    if (shapeSel.size > 0) {
      out = out.filter((p) =>
        (p.diamond_lines || []).some((ln) => shapeSel.has(String(ln.shape)))
      );
    }
    if (sort) {
      const withPrice = out.map((p) => {
        const b2b = locked ? computeSelling(p, settings, ratecard, "b2b") : null;
        const cust = locked ? computeSelling(p, settings, ratecard, "customer") : null;
        return {
          p,
          b2b: Number(effectiveTierPrice(p, "b2b", b2b?.selling_price) || 0),
          cust: Number(effectiveTierPrice(p, "customer", cust?.selling_price) || 0),
          cost: Number(p.cost_total || 0),
          gold: Number(p.net_wt_g || 0),
          dia: Number(p.dia_wt_ct || 0),
        };
      });
      const cmp = {
        price_b2b_desc:  (a, b) => b.b2b - a.b2b,
        price_b2b_asc:   (a, b) => a.b2b - b.b2b,
        price_cust_desc: (a, b) => b.cust - a.cust,
        price_cust_asc:  (a, b) => a.cust - b.cust,
        price_cost_desc: (a, b) => b.cost - a.cost,
        price_cost_asc:  (a, b) => a.cost - b.cost,
        gold_wt_desc:    (a, b) => b.gold - a.gold,
        gold_wt_asc:     (a, b) => a.gold - b.gold,
        dia_wt_desc:     (a, b) => b.dia - a.dia,
        dia_wt_asc:      (a, b) => a.dia - b.dia,
      }[sort];
      if (cmp) { withPrice.sort(cmp); out = withPrice.map((x) => x.p); }
    }
    return out;
  })();
  const activeFilterCount = shapeSel.size;
  const visibleCodes = items.map((p) => p.jewel_code);
  const allVisibleSelected = visibleCodes.length > 0 && visibleCodes.every((c) => selected.has(c));
  const toggleOne = (jc) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(jc)) next.delete(jc); else next.add(jc);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      visibleCodes.forEach((c) => next.add(c));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  return (
    <div className="min-h-screen bg-[#FAF8F3] pb-[max(env(safe-area-inset-bottom),6rem)]" data-testid="wholesaler-portal">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#0D1B2A]/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C]">Wholesaler</div>
            <h1 className="font-serif text-xl text-[#0D1B2A] truncate">{stock.recipient_name}</h1>
          </div>
          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#0D1B2A]/5 text-[#0D1B2A]/70 font-mono">
            {currency}
          </span>
          <button
            onClick={() => setRlOpen(true)}
            className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 bg-white hover:bg-[#FAF8F3] text-sm flex items-center gap-1.5"
            data-testid="open-retailer-links"
            title="Retailer Links — password-protected shopfronts for your buyers"
          >
            <Link2 className="w-4 h-4" /> <span className="hidden sm:inline">Retailer links</span>
          </button>
          <button
            onClick={() => setRatesOpen(true)}
            className={`relative px-3 py-2 rounded-md border text-sm flex items-center gap-1.5 ${
              locked ? "border-[#0D1B2A]/15 bg-white hover:bg-[#FAF8F3]" : "border-[#C9A84C] bg-[#C9A84C]/10 text-[#0D1B2A]"
            }`}
            data-testid="open-rates"
          >
            <Settings2 className="w-4 h-4" /> <span className="hidden sm:inline">{locked ? "Rates" : "Set rates"}</span>
            {!locked && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />}
          </button>
        </div>

        {/* Lock banner */}
        {!locked && (
          <div className="bg-amber-50 border-t border-amber-200 text-amber-900 text-sm px-4 py-2 max-w-3xl mx-auto" data-testid="lock-in-banner">
            <Sparkles className="inline w-4 h-4 mr-1.5 -mt-0.5" />
            Costs are visible. Set &amp; lock in your selling rates to see live selling prices on every piece.
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Search + filters */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[180px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by jewel code or style…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-11"
              data-testid="portal-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="portal-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-11" data-testid="portal-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available" data-testid="portal-status-available">Available</SelectItem>
              <SelectItem value="in_stock" data-testid="portal-status-in_stock">In stock only</SelectItem>
              <SelectItem value="design" data-testid="portal-status-design">Design only</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
              <SelectItem value="memo">Memo</SelectItem>
              <SelectItem value="returned">Returned</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-32 h-11" data-testid="portal-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* CS 21.39 -- Sort */}
          <div className="flex items-center gap-1.5">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
            <Select value={sort || "none"} onValueChange={(v) => setSort(v === "none" ? "" : v)}>
              <SelectTrigger className="w-[210px] h-11" data-testid="portal-sort-trigger"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Default order</SelectItem>
                {visibility.cost && <SelectItem value="price_cost_desc" data-testid="portal-sort-price_cost_desc">Cost — High to Low</SelectItem>}
                {visibility.cost && <SelectItem value="price_cost_asc"  data-testid="portal-sort-price_cost_asc">Cost — Low to High</SelectItem>}
                {visibility.b2b  && <SelectItem value="price_b2b_desc"  data-testid="portal-sort-price_b2b_desc">B2B — High to Low</SelectItem>}
                {visibility.b2b  && <SelectItem value="price_b2b_asc"   data-testid="portal-sort-price_b2b_asc">B2B — Low to High</SelectItem>}
                {visibility.sell && <SelectItem value="price_cust_desc" data-testid="portal-sort-price_cust_desc">Sell — High to Low</SelectItem>}
                {visibility.sell && <SelectItem value="price_cust_asc"  data-testid="portal-sort-price_cust_asc">Sell — Low to High</SelectItem>}
                <SelectItem value="gold_wt_desc" data-testid="portal-sort-gold_wt_desc">Gold weight — High to Low</SelectItem>
                <SelectItem value="gold_wt_asc"  data-testid="portal-sort-gold_wt_asc">Gold weight — Low to High</SelectItem>
                <SelectItem value="dia_wt_desc"  data-testid="portal-sort-dia_wt_desc">Diamond weight — High to Low</SelectItem>
                <SelectItem value="dia_wt_asc"   data-testid="portal-sort-dia_wt_asc">Diamond weight — Low to High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* CS 21.39 -- Shape filter (multi-select, in a Sheet on mobile) */}
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <button
                className={`h-11 px-3 rounded-md border text-sm flex items-center gap-1.5 transition ${
                  activeFilterCount > 0
                    ? "border-[#0D1B2A] bg-[#0D1B2A]/5 text-[#0D1B2A]"
                    : "border-[#0D1B2A]/15 bg-white hover:bg-[#FAF8F3] text-slate-700"
                }`}
                data-testid="portal-filter-open"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">Shapes</span>
                {activeFilterCount > 0 && (
                  <span className="text-[10px] rounded-full bg-[#0D1B2A] text-white px-1.5 py-0.5 leading-none">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#FAF8F3]" data-testid="portal-filter-sheet">
              <SheetHeader className="text-left mb-6">
                <SheetTitle className="font-serif text-2xl">Filter by diamond shape</SheetTitle>
              </SheetHeader>
              <div className="space-y-3">
                {shapeOptions.length === 0 && (
                  <p className="text-sm text-slate-400">No diamond pieces in the current view.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {shapeOptions.map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleShape(s)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition ${
                        shapeSel.has(s)
                          ? "bg-[#0D1B2A] text-white border-[#0D1B2A]"
                          : "bg-transparent text-[#0D1B2A]/75 border-[#0D1B2A]/20 hover:border-[#0D1B2A]/50"
                      }`}
                      data-testid={`portal-filter-shape-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => setShapeSel(new Set())}
                    className="text-[11px] text-slate-500 hover:text-[#0D1B2A] underline underline-offset-4"
                    data-testid="portal-filter-clear"
                  >
                    Clear shape filters
                  </button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Selection / Live-preview bar */}
        <div className="flex items-center justify-between text-xs text-slate-500 px-1 gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleAll}
              disabled={items.length === 0}
              className="text-[#0D1B2A] underline-offset-2 hover:underline disabled:opacity-40 disabled:no-underline"
              data-testid="select-all-visible"
            >
              {allVisibleSelected ? "Clear selection" : "Select all"} ({visibleCodes.length})
            </button>
            <span data-testid="select-counter">
              {selected.size} of {items.length} selected
            </span>
          </div>
          {/* CS 21.11 / Turn F-2 -- three independent visibility toggles */}
          <div className="flex items-center gap-3" data-testid="visibility-rail">
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="vis-toggle-cost">
              <Switch checked={visibility.cost} onCheckedChange={() => toggleVis("cost")} />
              <span>Cost</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="vis-toggle-b2b">
              <Switch checked={visibility.b2b} onCheckedChange={() => toggleVis("b2b")} />
              <span>B2B</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="vis-toggle-sell">
              <Switch checked={visibility.sell} onCheckedChange={() => toggleVis("sell")} />
              <span>Sell</span>
            </label>
          </div>
        </div>

        {/* Empty */}
        {items.length === 0 && (
          <div className="luxe-card p-10 text-center text-slate-400" data-testid="portal-empty">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
            <p>No pieces match.</p>
          </div>
        )}

        {/* Piece cards with image + selection checkbox */}
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((p) => {
            const b2b = locked ? computeSelling(p, settings, ratecard, "b2b") : null;
            const cust = locked ? computeSelling(p, settings, ratecard, "customer") : null;
            const b2bShown = effectiveTierPrice(p, "b2b", b2b?.selling_price);
            const custShown = effectiveTierPrice(p, "customer", cust?.selling_price);
            const b2bK = tierOverrideKind(p, "b2b");
            const custK = tierOverrideKind(p, "customer");
            const isChecked = selected.has(p.jewel_code);
            return (
              <div
                key={p.jewel_code}
                className={`relative rounded-lg border bg-white overflow-hidden transition-colors flex flex-col ${
                  isChecked ? "border-[#C9A84C] ring-1 ring-[#C9A84C]/40" : "border-[#0D1B2A]/10 hover:border-[#C9A84C]/60"
                }`}
                data-testid={`piece-${p.jewel_code}`}
              >
                {/* Checkbox overlay */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleOne(p.jewel_code); }}
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                    isChecked ? "bg-[#C9A84C] border-[#C9A84C]" : "bg-white/90 border-slate-300 hover:border-[#C9A84C]"
                  }`}
                  data-testid={`select-${p.jewel_code}`}
                  aria-label={isChecked ? "Deselect" : "Select"}
                >
                  {isChecked && <Check className="w-3.5 h-3.5 text-white" />}
                </button>
                {/* CS 21.70 -- PIN-gated Delete button (top-right). Rendered only
                    when HO has provisioned a delete PIN for this wholesaler. */}
                {meta?.delete_pin_available && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(p.jewel_code); }}
                    className="absolute top-2 right-2 z-10 w-7 h-7 rounded-md bg-white/95 border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50"
                    title="Delete this piece"
                    aria-label={`Delete ${p.jewel_code}`}
                    data-testid={`delete-btn-${p.jewel_code}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setActivePiece(p)}
                  className="text-left flex flex-col gap-2 w-full"
                  data-testid={`open-${p.jewel_code}`}
                >
                  <PieceImage
                    src={p.image_url}
                    alt={p.jewel_code}
                    className="w-full aspect-square"
                    testid={`piece-image-${p.jewel_code}`}
                  />
                  {/* CS 21.42 -- Made-to-order red dot for design pieces. */}
                  {p.stock_status === "design" && (
                    <div
                      className="absolute bottom-3 right-3 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white shadow-sm"
                      title="Made to order — priced through this wholesaler's rate card"
                      data-testid={`wp-mto-${p.jewel_code}`}
                    />
                  )}
                  <div className="px-4 pb-4 pt-2 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-slate-500 truncate">{p.style_no || "—"}</div>
                        <div className="font-medium text-[#0D1B2A] truncate">{p.jewel_code}</div>
                      </div>
                      <StatusPill status={p.stock_status || "in_stock"} />
                    </div>
                    <div className="text-xs text-slate-500 tabular-nums" data-testid={`piece-spec-${p.jewel_code}`}>
                      {[
                        p.category,
                        p.karat ? `${p.karat}KT` : (p.metal || null),
                        p.net_wt_g > 0 ? `${formatWeight(p.net_wt_g)} g` : null,
                        Number(p.dia_wt_ct) > 0 ? `${Number(p.dia_wt_ct).toFixed(2)} ct` : null,
                      ].filter(Boolean).join(" · ")}
                    </div>
                    {(Number(p.dia_pcs) > 0 || Number(p.dia_wt_ct) > 0) && (
                      <div className="text-[11px] text-slate-500 flex items-center gap-1.5 -mt-0.5" data-testid={`piece-diamonds-${p.jewel_code}`}>
                        <Sparkles className="w-3 h-3 text-[#C9A84C]" />
                        <span className="tabular-nums">{Number(p.dia_pcs) || 0} pcs · {Number(p.dia_wt_ct || 0).toFixed(3)} ct</span>
                      </div>
                    )}
                    {p.stock_status === "memo" && p.memo_client_name && (
                      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md inline-flex items-center gap-1"
                           data-testid={`memo-client-card-${p.jewel_code}`}>
                        <Tag className="w-3 h-3" /> out to <span className="font-medium">{p.memo_client_name}</span>
                        {p.memo_date && (
                          <span className="ml-1 text-amber-700 font-normal" data-testid={`memo-aging-card-${p.jewel_code}`}>
                            · {Math.max(0, Math.floor((Date.now() - new Date(p.memo_date).getTime()) / 86400000))}d
                          </span>
                        )}
                      </div>
                    )}
                    {/* CS 21.11 / Turn F-2 -- three price columns gated by
                        independent visibility toggles (Cost / B2B / Sell). */}
                    <div className={`grid ${[visibility.cost, visibility.b2b, visibility.sell].filter(Boolean).length === 3 ? "grid-cols-3" : [visibility.cost, visibility.b2b, visibility.sell].filter(Boolean).length === 2 ? "grid-cols-2" : "grid-cols-1"} gap-2 mt-1 text-sm`} data-testid={`card-prices-${p.jewel_code}`}>
                      {visibility.cost && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">Cost</div>
                          <div className="font-mono">{fmtMoney(p.cost_total, currency)}</div>
                        </div>
                      )}
                      {visibility.b2b && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">B2B</div>
                          {locked ? (
                            <>
                              <div className="font-mono text-[#0D1B2A]" data-testid={`card-b2b-${p.jewel_code}`}>{fmtMoney(b2bShown, currency)}</div>
                              {b2bK !== "none" && (
                                <div className="text-[9px] text-amber-600 italic mt-0.5"
                                     data-testid={`card-b2b-badge-${p.jewel_code}`}>
                                  {b2bK === "locked" ? "Manual · Locked" : "Manual"}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-[10px] text-amber-700 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> set rates
                            </div>
                          )}
                        </div>
                      )}
                      {visibility.sell && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-400">Sell</div>
                          {locked ? (
                            <>
                              <div className="font-mono text-[#0D1B2A] font-semibold" data-testid={`card-sell-${p.jewel_code}`}>{fmtMoney(custShown, currency)}</div>
                              {custK !== "none" && (
                                <div className="text-[9px] text-amber-600 italic mt-0.5"
                                     data-testid={`card-sell-badge-${p.jewel_code}`}>
                                  {custK === "locked" ? "Manual · Locked" : "Manual"}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-[10px] text-amber-700 flex items-center gap-1">
                              <Lock className="w-3 h-3" /> set rates
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                      Open <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </main>

      {/* Floating selection bar — lifted above the Emergent badge */}
      {(selected.size > 0 || items.length > 0) && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-20 bg-[#0D1B2A] text-white rounded-full shadow-2xl flex items-center gap-2 pl-4 pr-2 py-2 whitespace-nowrap"
          style={{ bottom: "max(env(safe-area-inset-bottom), 5rem)" }}
          data-testid="selection-bar"
        >
          <span className="text-sm tabular-nums" data-testid="selection-count">{selected.size} selected</span>
          <button
            onClick={allVisibleSelected ? clearSelection : toggleAll}
            disabled={items.length === 0}
            className="text-xs underline-offset-2 hover:underline text-white/80 px-2 disabled:opacity-40"
            data-testid="selection-select-all"
          >
            {allVisibleSelected ? "Clear selection" : "Select all"}
          </button>
          {selected.size > 0 && !allVisibleSelected && (
            <button
              onClick={clearSelection}
              className="text-xs underline-offset-2 hover:underline text-white/60 px-1"
              data-testid="selection-clear"
            >
              clear
            </button>
          )}
          <button
            disabled={selected.size === 0}
            onClick={() => setShareOpen(true)}
            className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white text-sm font-medium px-4 py-2 rounded-full flex items-center gap-1.5 disabled:opacity-50"
            data-testid="share-open"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      )}

      <SellingRatesSheet
        open={ratesOpen}
        onOpenChange={setRatesOpen}
        token={token}
        pin={pin}
        settings={settings}
        currency={currency}
        onSaved={(d) => { setSettings(d); refresh(); }}
      />
      <PieceDetail
        piece={activePiece}
        settings={settings}
        ratecard={ratecard}
        currency={currency}
        locked={locked}
        token={token}
        pin={pin}
        recipientName={stock.recipient_name}
        visibility={visibility}
        onChanged={async () => {
          // Turn F-0 -- refresh stock list AND re-fetch the currently-open
          // piece so the drawer shows the fresh state (status, image, memo/
          // sold snapshot) instead of closing.
          const jc = activePiece?.jewel_code;
          await refresh();
          if (jc) {
            try {
              const { data } = await pub.get(`/w/${token}/stock/${jc}`, { params: { pin } });
              setActivePiece(data);
            } catch {
              // Piece was filtered out (e.g. status changed by another user);
              // drawer just stays with the stale copy until the user closes it.
            }
          }
        }}
        onClose={() => setActivePiece(null)}
      />
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        token={token}
        pin={pin}
        selected={Array.from(selected)}
        recipientName={stock.recipient_name}
      />
      <RetailerLinksModal
        open={rlOpen}
        onOpenChange={setRlOpen}
        token={token}
        pin={pin}
      />
      {/* CS 21.70 -- PIN-gated soft-delete confirm dialog. */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        token={token}
        pin={pin}
        jewelCode={deleteTarget || ""}
        onDeleted={() => { setDeleteTarget(null); refresh(); }}
      />
    </div>
  );
}
